/**
 * PublicInterviewPage — the candidate-facing proctored interview flow.
 * Unauthenticated: the URL's `token` is the credential. Three screens in
 * one component, switched by session status: verify details -> take the
 * configured questions (timed) -> thank you. Reopening a completed link
 * always lands back on the thank-you screen (see getVerifyScreen/status).
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { toast } from 'react-hot-toast'
import {
  CheckCircleIcon, ClockIcon, ShieldCheckIcon, UserCircleIcon,
  BriefcaseIcon, BuildingOffice2Icon, CodeBracketIcon, ChatBubbleLeftRightIcon,
  PencilSquareIcon, VideoCameraIcon, ComputerDesktopIcon, ExclamationTriangleIcon,
  FlagIcon, ShieldExclamationIcon, InformationCircleIcon, MicrophoneIcon,
  HomeIcon, LightBulbIcon, SpeakerXMarkIcon, FaceSmileIcon, ArrowsPointingOutIcon, UserIcon,
  ClipboardDocumentIcon, UserGroupIcon, DevicePhoneMobileIcon, EyeSlashIcon, VideoCameraSlashIcon,
  PhoneIcon, EnvelopeIcon, StarIcon, CloudArrowUpIcon,
} from '@heroicons/react/24/outline'
import { FlagIcon as FlagIconSolid, CheckIcon as CheckIconSolid, ExclamationTriangleIcon as ExclamationTriangleIconSolid, StarIcon as StarIconSolid } from '@heroicons/react/24/solid'

import LoadingSpinner from '@/components/common/LoadingSpinner'
import Tooltip from '@/components/common/Tooltip'
import {
  getVerifyScreen, verifyCandidate, startInterview, submitAnswer, submitAnswerBeacon, completeInterview,
  runCode, runTestCases, reportProctoringViolation, reportIntegrityEvent, uploadRecording, submitFeedback,
  abandonInterview, abandonInterviewBeacon,
  type VerifyScreen, type PublicQuestion, type RunCodeResult, type TestCaseRunResult,
  type RunTestCasesResult, type IntegrityEventType,
} from '@/services/proctoredInterviewPublicApi'
import WhiteboardCanvas, { type WhiteboardCanvasHandle } from '@/components/proctoredInterview/WhiteboardCanvas'
import useMediaRecorder from '@/hooks/useMediaRecorder'
import useIntegrityMonitor from '@/hooks/useIntegrityMonitor'
import useIdCaptureGuide from '@/hooks/useIdCaptureGuide'
import LegalDocumentModal from '@/components/common/LegalDocumentModal'
import { TERMS_OF_SERVICE_MD } from '@/content/legal/termsOfService'
import { PRIVACY_POLICY_MD } from '@/content/legal/privacyPolicy'
import useDictation from '@/hooks/useDictation'

// 'interrupted' is terminal for this link: the attempt was closed by the
// server-side sweeper after going inactive. A re-issued link is a different
// session with its own token, so this one keeps showing that screen.
type Stage = 'loading' | 'verify' | 'precheck' | 'id_verification' | 'exam_tips' | 'in_progress' | 'submitting' | 'done' | 'interrupted' | 'error'
type MonitorCheckState = 'unchecked' | 'single' | 'multiple' | 'unsupported'

// How long the candidate may stay outside fullscreen before the attempt is
// abandoned outright. Distinct from the 5-second grace in the violation
// overlay, which only decides whether an episode costs a strike: this is the
// much longer "they have actually left" threshold. Mirrors the backend's
// PROCTORED_INTERVIEW_FULLSCREEN_ABSENCE_LIMIT_SECONDS (60s).
const FULLSCREEN_ABSENCE_LIMIT_MS = 60_000

// Answer saving: tries per save before giving up (the next save-all retries it).
const ANSWER_SAVE_ATTEMPTS = 3

interface AnswerDraft {
  text: string
  metadata: Record<string, any>
  markedForReview: boolean
}

interface IntegrityLogEntry {
  type: IntegrityEventType
  at: number
}

const INTEGRITY_EVENT_LABELS: Record<IntegrityEventType, string> = {
  multiple_persons: 'Multiple people detected',
  no_person: 'No one in view',
  electronic_device: 'Electronic device detected',
  looking_away: 'Looking away from screen',
  camera_blocked: 'Camera appears blocked',
  multiple_audio_sources: 'Multiple voices detected',
}

// "Tips for Exam" screen, shown right before the interview actually starts.
// The last two are reworded from the usual proctoring-vendor phrasing
// ("Stay Within VDI Window" / "Close Proctortrack at the End") to match
// what this app actually enforces (fullscreen/tab-switch violations) rather
// than referencing infrastructure this app doesn't use. `imageSrc` (free,
// no-attribution-required unDraw illustrations under /public/images/tips)
// is shown when present; `icon` is the fallback for the one tip ("noise")
// with no good illustration match.
const EXAM_TIPS: { icon: React.FC<{ className?: string }>; imageSrc?: string; label: string }[] = [
  { icon: HomeIcon, imageSrc: '/images/tips/quiet-room.svg', label: 'Use a quiet room with no distractions and a clean desk' },
  { icon: VideoCameraIcon, imageSrc: '/images/tips/webcam.svg', label: 'Sit directly in front of your webcam' },
  { icon: LightBulbIcon, imageSrc: '/images/tips/lighting.svg', label: 'Create the proper lighting' },
  { icon: SpeakerXMarkIcon, label: 'Turn off noise-making devices' },
  { icon: FaceSmileIcon, imageSrc: '/images/tips/face-visible.svg', label: 'Ensure your face is clearly visible' },
  { icon: ArrowsPointingOutIcon, imageSrc: '/images/tips/fullscreen.svg', label: "Don't switch tabs or exit fullscreen" },
  { icon: UserIcon, imageSrc: '/images/tips/alone.svg', label: 'No one else is allowed in the room' },
  { icon: CheckCircleIcon, imageSrc: '/images/tips/done.svg', label: 'Submit and close the tab when you\'re done' },
]

// "Test Guidelines" screen, shown after Tips and right before the real
// start. Deliberately limited to rules this app can actually back up —
// either something it enforces in real time (copy/paste block, fullscreen
// tracking) or something the always-on screen/camera/mic recording could
// surface on review — rather than copying a vendor's full checklist
// wholesale and claiming detection (e.g. print-screen, physical books,
// calculators) this app has no way to actually check.
const TEST_GUIDELINES: { icon: React.FC<{ className?: string }>; label: string }[] = [
  { icon: ClipboardDocumentIcon, label: 'Copy, cut, and paste are disabled during the interview' },
  { icon: ArrowsPointingOutIcon, label: 'Exiting fullscreen or switching tabs is recorded as a violation' },
  { icon: UserGroupIcon, label: 'Multiple people in view is recorded as a violation' },
  { icon: UserIcon, label: 'Stepping out of view of your camera is recorded as a violation' },
  { icon: DevicePhoneMobileIcon, label: 'Other electronic devices in view are recorded as a violation' },
  { icon: EyeSlashIcon, label: 'Looking away from the screen for extended periods is recorded' },
  { icon: VideoCameraSlashIcon, label: 'Blocking or covering your camera is recorded as a violation' },
  { icon: ChatBubbleLeftRightIcon, label: 'Multiple voices/talking to someone else is recorded as a violation' },
  { icon: ClockIcon, label: 'Closing the tab or staying inactive for more than 5 minutes will mark your interview as incomplete' },
]

export default function PublicInterviewPage() {
  const { token } = useParams<{ token: string }>()
  const [stage, setStage] = useState<Stage>('loading')
  const [screen, setScreen] = useState<VerifyScreen | null>(null)
  const [questions, setQuestions] = useState<PublicQuestion[]>([])
  const [questionIndex, setQuestionIndex] = useState(0)
  // Mirrors for the answer-saving helpers (see saveQuestion).
  const questionsRef = useRef(questions)
  questionsRef.current = questions
  const questionIndexRef = useRef(questionIndex)
  questionIndexRef.current = questionIndex
  const [answers, setAnswers] = useState<Record<string, AnswerDraft>>({})
  const [errorMessage, setErrorMessage] = useState('')
  const [starting, setStarting] = useState(false)
  const [submittingAnswer, setSubmittingAnswer] = useState(false)
  const whiteboardRef = useRef<WhiteboardCanvasHandle>(null)
  // Accumulated per-question elapsed time, since free navigation between
  // questions means "time spent" is no longer a single start->next window —
  // a question can be visited, left, and revisited multiple times.
  const timeSpentRef = useRef<Record<string, number>>({})
  // Kept fresh every render (see the effect near currentQuestion below) so
  // the beforeunload handler — set up once and not re-registered on every
  // keystroke — can still read the CURRENT question/draft/token at the
  // actual moment the tab closes, not stale values from when it was set up.
  const latestDraftRef = useRef<{ token: string | null; question: PublicQuestion | null; draft: AnswerDraft | null; timeSpent: number }>({
    token: null, question: null, draft: null, timeSpent: 0,
  })
  const questionEnteredAtRef = useRef<{ id: string | null; at: number | null }>({ id: null, at: null })

  // ---- Screen+audio recording (mandatory prerequisite) & camera/mic integrity events ----
  const [integrityLog, setIntegrityLog] = useState<IntegrityLogEntry[]>([])
  const [cameraVideoEl, setCameraVideoEl] = useState<HTMLVideoElement | null>(null)
  // Resolved once the in-flight stopRecording()'s resulting blob has been
  // uploaded (success or failure — either way we stop waiting) — lets
  // handleSubmitInterview/the auto-submit path actually wait for the
  // upload instead of firing it and immediately showing "Thank You" while
  // it's still in flight, which is exactly how a recording never arrives:
  // the candidate closes the tab the moment they see that screen, killing
  // the still-running background upload.
  const pendingRecordingStopResolveRef = useRef<(() => void) | null>(null)
  // How often (ms) each recording chunk is uploaded — overwritten from the
  // start/state response's recording_chunk_interval_seconds; 300000 (5min)
  // matches the backend's own default so early chunks (before that response
  // lands) still upload on a sane cadence rather than never.
  const [recordingChunkIntervalMs, setRecordingChunkIntervalMs] = useState(300000)
  // Chained (not parallel) so chunks land at the server in order even if
  // one upload is slow — each new chunk's upload waits for the previous
  // one to settle before starting.
  const lastChunkUploadRef = useRef<Promise<void>>(Promise.resolve())
  const recorder = useMediaRecorder({
    timesliceMs: recordingChunkIntervalMs,
    onDataAvailable: (chunk) => {
      if (!token) return
      lastChunkUploadRef.current = lastChunkUploadRef.current.then(() =>
        uploadRecording(token, chunk).catch(() => console.warn('Recording chunk upload failed — continuing, scoring does not depend on it.'))
      )
    },
    onStop: () => {
      // stop() flushes one last (usually partial) chunk through
      // onDataAvailable before this fires — awaiting the chain here (rather
      // than uploading anything ourselves) makes sure that final chunk is
      // confirmed uploaded before Submit proceeds to /complete.
      const resolvePendingStop = pendingRecordingStopResolveRef.current
      pendingRecordingStopResolveRef.current = null
      lastChunkUploadRef.current.finally(() => resolvePendingStop?.())
    },
    onError: (err) => toast.error(err.message || 'Screen recording failed.'),
    // The candidate is recording themselves alone, not sharing a live call —
    // their voice only ever reaches micStream (already checked earlier in
    // startRecording()), never the shared system-audio track. Waiting for
    // system audio to go audible here would never resolve.
    requireAudibleSystemAudio: false,
  })

  // Stops the recorder (if running) and waits for its upload to settle —
  // await this before ever moving to 'done', on both the explicit-submit
  // and auto-submit(3 violations) paths.
  const finishRecordingUpload = async () => {
    if (!recorder.isRecording) return
    await new Promise<void>((resolve) => {
      pendingRecordingStopResolveRef.current = resolve
      recorder.stopRecording()
    })
  }

  // Mirrors for the answer-saving helpers (see saveQuestion), which also run
  // from timers/proctoring handlers holding an older render's closure.
  const answersRef = useRef(answers)
  answersRef.current = answers
  // What was last successfully saved per question, so unchanged answers
  // aren't re-sent and a failed save is retried later.
  const savedSignatureRef = useRef<Record<string, string>>({})
  const saveAllUnsavedRef = useRef<() => Promise<boolean>>(async () => true)
  const timeUpNotifiedRef = useRef<Set<string>>(new Set())

  const currentDraft = (questionId: string): AnswerDraft =>
    answers[questionId] || { text: '', metadata: {}, markedForReview: false }

  const updateDraft = (questionId: string, patch: Partial<AnswerDraft>) => {
    setAnswers((prev) => ({ ...prev, [questionId]: { ...currentDraft(questionId), ...patch } }))
  }

  // ---- Pre-start device/permission check + throughout-interview proctoring ----
  const [mediaStream, setMediaStream] = useState<MediaStream | null>(null)
  const [mediaError, setMediaError] = useState('')
  const [monitorCheck, setMonitorCheck] = useState<MonitorCheckState>('unchecked')
  const [geo, setGeo] = useState<{ lat: number; lng: number; accuracy_meters?: number } | null>(null)
  // Consent checkbox on the initial "Consent to AI-Assisted Proctoring"
  // verify screen — gates Continue the same way the precheck's device
  // checks gate Start.
  const [policyAgreed, setPolicyAgreed] = useState(false)
  // Which legal document, if any, is open over the consent screen. The
  // documents themselves are static content modules (src/content/legal);
  // nothing here is fetched.
  const [openLegalDoc, setOpenLegalDoc] = useState<'terms' | 'privacy' | null>(null)
  // Guided tour shown once, right after the candidate starts the interview.
  // null = inactive, 0 = intro card, 1..TOUR_STEPS.length = a spotlighted step.
  const [tourStep, setTourStep] = useState<number | null>(null)
  const [fullscreenLost, setFullscreenLost] = useState(false)
  const [violationCount, setViolationCount] = useState(0)
  const [graceSecondsLeft, setGraceSecondsLeft] = useState<number | null>(null)
  const [violationReportFailed, setViolationReportFailed] = useState(false)
  const [lastViolationType, setLastViolationType] = useState<string>('fullscreen_exit')
  const precheckVideoRef = useRef<HTMLVideoElement>(null)
  // The face+ID photo captured on the "ID Verification" screen — carried
  // forward and sent with the real startInterview() call on the Tips screen,
  // rather than snapping a fresh (and by then unrelated) frame at that point.
  const [idPhotoSnapshot, setIdPhotoSnapshot] = useState<string | null>(null)
  const idVerificationVideoRef = useRef<HTMLVideoElement>(null)
  // Mirrors idVerificationVideoRef.current into state — a plain ref's
  // .current isn't available until after the render that mounts it, so
  // useIdCaptureGuide (which needs the element as a prop to react to it)
  // is handed this instead of reading the ref directly during render.
  const [idVideoEl, setIdVideoEl] = useState<HTMLVideoElement | null>(null)
  // The Tips/Guidelines screen shows no camera preview at all, but the
  // camera is still live and this screen has no time limit — a visually
  // hidden <video> keeps useIntegrityMonitor fed here too (see its `active`
  // comment) without adding a camera box this screen was never asked for.
  const examTipsVideoRef = useRef<HTMLVideoElement>(null)

  const integrityMonitor = useIntegrityMonitor({
    videoEl: cameraVideoEl,
    micStream: mediaStream,
    // Detection itself (not just model preload) now runs from the precheck
    // screen onward, the moment the candidate's camera stream is live - not
    // only once the interview officially starts. Without this there was a
    // real window (from granting the camera through clicking Start, plus
    // however long question 1 took to render) where nothing was actually
    // being monitored despite the camera already being on. This must cover
    // EVERY stage between precheck and in_progress, not just precheck
    // itself — id_verification and exam_tips both have unbounded, candidate-
    // controlled dwell time with the camera already live; leaving either out
    // reopens exactly the gap this comment describes.
    active: stage === 'precheck' || stage === 'id_verification' || stage === 'exam_tips' || stage === 'in_progress',
    preload: stage === 'precheck',
    onEvent: (type, details) => {
      setIntegrityLog((log) => [{ type, at: Date.now() }, ...log].slice(0, 30))
      if (token) reportIntegrityEvent(token, type, details).catch(() => {})
    },
  })

  // Binds the live stream to the ID Verification screen's <video> via a
  // stable ref + effect, NOT an inline `ref={(el) => ...}` callback — an
  // inline arrow function gets a new identity every render, which makes
  // React detach/reattach the ref (and re-null srcObject) constantly,
  // causing the on/off flicker. See CameraPreview below for the same fix.
  useEffect(() => {
    if (stage !== 'id_verification' || idPhotoSnapshot) { setIdVideoEl(null); return }
    const el = idVerificationVideoRef.current
    if (el) el.srcObject = mediaStream
    setIdVideoEl(el)
    // Also feeds useIntegrityMonitor above — this screen's camera is live
    // and open-ended (candidate can stay on it as long as they like), so it
    // must be covered by the same continuous monitoring as precheck/in_progress.
    setCameraVideoEl(el)
  }, [stage, mediaStream, idPhotoSnapshot])

  // Binds the same stream to the Tips/Guidelines screen's hidden video —
  // same stable-ref pattern as above, purely to keep monitoring fed there.
  // No "else" branch that nulls cameraVideoEl on other stages — the
  // precheck/id_verification effects own that state on their own stages,
  // and this effect running after them on the same render would otherwise
  // wipe out whichever one just set it (effects run in declaration order).
  useEffect(() => {
    if (stage !== 'exam_tips') return
    const el = examTipsVideoRef.current
    if (el) el.srcObject = mediaStream
    setCameraVideoEl(el)
  }, [stage, mediaStream])

  // Preload starts at 'precheck' rather than 'id_verification' — the model
  // download (~19MB of WASM + face/hand models) gets a head start during
  // the device-check screen instead of racing a fresh timeout once the
  // candidate reaches ID Verification.
  const idCaptureGuide = useIdCaptureGuide(idVideoEl, (stage === 'precheck' || stage === 'id_verification') && !idPhotoSnapshot)

  // One still frame from a live <video> element — best-effort, for the
  // hiring team's record only. Used on the ID Verification screen to
  // capture the candidate's face+ID photo.
  const captureSnapshotFromVideo = (video: HTMLVideoElement | null): string | null => {
    if (!video || !video.videoWidth) return null
    const canvas = document.createElement('canvas')
    canvas.width = video.videoWidth
    canvas.height = video.videoHeight
    const ctx = canvas.getContext('2d')
    if (!ctx) return null
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height)
    return canvas.toDataURL('image/png')
  }

  // Acquire camera/mic the moment the candidate reaches the device-check
  // screen, so the same live stream can be reused as the always-visible
  // self-view once the interview actually starts (re-requesting per-question
  // would re-prompt for permission and flicker the preview).
  useEffect(() => {
    if (stage !== 'precheck') return
    let cancelled = false
    // Echo cancellation requested explicitly rather than relying on the
    // browser's implicit default for a bare `audio: true` — the same three
    // constraints useMediaRecorder.ts already uses for this interview's actual
    // mic recording, so the precheck exercises the same capture path it will.
    navigator.mediaDevices?.getUserMedia({
      video: true,
      audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
    })
      .then((stream) => { if (!cancelled) setMediaStream(stream) })
      .catch(() => { if (!cancelled) setMediaError('Camera and microphone access is required to begin. Please allow access in your browser and try again.') })
    return () => { cancelled = true }
  }, [stage])

  // Binds the stream to the precheck row's small preview thumbnail via a
  // stable ref + effect — same fix as the ID Verification screen's video
  // (see its comment): an inline `ref={(el) => ...}` callback gets a new
  // identity every render, which makes React detach/reattach it constantly
  // and causes the thumbnail to flicker on/off.
  useEffect(() => {
    if (stage !== 'precheck') return
    if (precheckVideoRef.current) precheckVideoRef.current.srcObject = mediaStream
    setCameraVideoEl(precheckVideoRef.current)
  }, [stage, mediaStream])

  // Best-effort, silent — a candidate who declines this still proceeds;
  // it's a record for the hiring team, not a gate. A generous timeout
  // matters here: desktops without GPS resolve location over Wi-Fi/IP
  // triangulation, which routinely takes longer than a few seconds.
  useEffect(() => {
    if (stage !== 'precheck' || !navigator.geolocation) return
    navigator.geolocation.getCurrentPosition(
      (pos) => setGeo({ lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy_meters: pos.coords.accuracy }),
      (err) => console.warn('Geolocation unavailable:', err.message),
      { timeout: 15000, maximumAge: 60000 }
    )
  }, [stage])

  // Run the display check automatically on arrival, rather than making the
  // candidate press a button to be told everything is fine. The Check button
  // now only appears when an extra display WAS found — the one case where
  // there is something for them to do (disconnect it) and re-check.
  //
  // Browsers without the Window Management API (currently Firefox, Safari)
  // have no equivalent way to enumerate displays at all, so there is nothing
  // to click there either; those fall straight to "unsupported" so Start
  // isn't gated on an impossible check.
  useEffect(() => {
    if (stage !== 'precheck') return
    if (typeof (window as any).getScreenDetails !== 'function') {
      setMonitorCheck('unsupported')
      return
    }
    handleCheckDisplays()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage])

  const handleCheckDisplays = async () => {
    const getScreenDetails = (window as any).getScreenDetails
    if (typeof getScreenDetails !== 'function') { setMonitorCheck('unsupported'); return }
    try {
      const details = await getScreenDetails()
      setMonitorCheck(details.screens.length > 1 ? 'multiple' : 'single')
    } catch {
      setMonitorCheck('unsupported')
    }
  }

  // Release the camera/mic and leave fullscreen once the interview is over —
  // nothing should keep recording after the candidate is done. Both paths
  // that reach 'done' (explicit submit, auto-submit-on-violations) already
  // stop the recorder and await its upload via finishRecordingUpload()
  // before getting here — this is just a safety net in case anything ever
  // reaches 'done' another way.
  useEffect(() => {
    if (stage !== 'done') return
    mediaStream?.getTracks().forEach((t) => t.stop())
    if (recorder.isRecording) recorder.stopRecording()
    if (document.fullscreenElement) document.exitFullscreen().catch(() => {})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage, mediaStream])

  useEffect(() => {
    return () => { mediaStream?.getTracks().forEach((t) => t.stop()) }
  }, [mediaStream])

  // The two things that are actually real, browser-confirmed signals: (1)
  // fullscreen state, (2) whether the tab is genuinely hidden (Page
  // Visibility API — true on tab-switch, minimize, or Alt-Tab to another
  // app in effectively every browser/OS combo). Deliberately NOT using
  // window.blur as an additional trigger — it fires on harmless things
  // like clicking the address bar or a devtools panel, which would punish
  // candidates for not actually leaving the assessment. Losing either
  // signal is one violation; three of them (of either kind, combined)
  // auto-submits server-side.
  //
  // Three things this guards against:
  // 1. A candidate hitting Esc (or Alt-Tabbing) in the gap between
  //    requestFullscreen() resolving and this effect mounting (i.e. before
  //    `stage` flips to 'in_progress') would slip past before any listener
  //    existed to notice — so on mount this also checks the CURRENT state
  //    directly, not just future events.
  // 2. Repeating the same exit (mashing Esc, or leaving the screen locked)
  //    must count as exactly one violation per continuous "left the
  //    assessment" episode, not zero (nothing to fire once already-exited)
  //    and not once-per-event. A 5s grace countdown also gives an
  //    accidental, quickly-corrected exit a chance to not count at all.
  // 3. Switching tabs almost always also exits fullscreen in the same
  //    instant — both signals firing together must still only produce ONE
  //    counted violation, not two.
  useEffect(() => {
    if (stage !== 'in_progress') return
    let graceTimer: ReturnType<typeof setTimeout> | null = null
    let countdownInterval: ReturnType<typeof setInterval> | null = null
    let reportedThisExit = false
    let exited = false

    // Fires when the candidate has been outside fullscreen long enough that
    // they have clearly left, rather than fumbled a key. Ends the attempt
    // outright — distinct from the 5-second grace below, which only decides
    // whether this episode costs a violation strike.
    let absenceTimer: ReturnType<typeof setTimeout> | null = null

    const clearTimers = () => {
      if (graceTimer) clearTimeout(graceTimer)
      if (countdownInterval) clearInterval(countdownInterval)
      if (absenceTimer) clearTimeout(absenceTimer)
      graceTimer = null
      countdownInterval = null
      absenceTimer = null
    }

    const onExit = (violationType: string) => {
      if (exited) return // already tracking this episode — a second signal (e.g. fullscreen exiting right after a tab switch) isn't a new one
      exited = true
      setFullscreenLost(true)
      setLastViolationType(violationType)
      setViolationReportFailed(false)
      reportedThisExit = false

      absenceTimer = setTimeout(async () => {
        clearTimers()
        if (!token) return
        try {
          // Save what they've entered first — once the attempt is ended the
          // server rejects answers, so anything unsaved would be lost.
          await saveAllUnsavedRef.current()
          await abandonInterview(token, violationType === 'tab_switch' ? 'left_tab' : 'left_fullscreen')
          // Straight to the interrupted screen. Not 'done': the candidate did
          // not submit, and telling them "thank you, submitted" would be a
          // lie about what just happened to their attempt.
          await finishRecordingUpload()
          setStage('interrupted')
        } catch {
          // If this fails the attempt stays in progress client-side, but the
          // server still ends it the moment they reopen the link.
        }
      }, FULLSCREEN_ABSENCE_LIMIT_MS)

      setGraceSecondsLeft(5)
      countdownInterval = setInterval(() => {
        setGraceSecondsLeft((s) => (s !== null && s > 0 ? s - 1 : 0))
      }, 1000)
      graceTimer = setTimeout(async () => {
        clearTimers()
        setGraceSecondsLeft(0)
        if (reportedThisExit || !token) return
        reportedThisExit = true
        try {
          // Save first: a 3rd strike completes the interview server-side,
          // after which answers are rejected.
          await saveAllUnsavedRef.current()
          const result = await reportProctoringViolation(token, violationType)
          setViolationCount(result.violations)
          if (result.auto_submitted) {
            setStage('submitting')
            await finishRecordingUpload()
            setStage('done')
          }
        } catch {
          // Surfaced in the overlay rather than silently claiming "recorded" —
          // a failed report must not look identical to a successful one.
          setViolationReportFailed(true)
        }
      }, 5000)
    }

    const onReturn = () => {
      exited = false
      clearTimers()
      setFullscreenLost(false)
      setGraceSecondsLeft(null)
      setViolationReportFailed(false)
    }

    const checkState = () => {
      // document.hidden alone can lag or race with the fullscreen-exit that
      // Chrome/Edge trigger automatically on tab-switch — window blur/focus
      // fires immediately and independently of that timing, so it's kept as
      // a second, redundant signal rather than relying on visibility alone.
      if (document.hidden || !document.hasFocus()) { onExit('tab_switch'); return }
      if (!document.fullscreenElement) { onExit('fullscreen_exit'); return }
      onReturn()
    }

    document.addEventListener('fullscreenchange', checkState)
    document.addEventListener('visibilitychange', checkState)
    window.addEventListener('blur', checkState)
    window.addEventListener('focus', checkState)
    checkState() // catch a pre-existing exit right away, not just future events

    return () => {
      clearTimers()
      document.removeEventListener('fullscreenchange', checkState)
      document.removeEventListener('visibilitychange', checkState)
      window.removeEventListener('blur', checkState)
      window.removeEventListener('focus', checkState)
    }
  }, [stage, token])

  const handleReturnToFullscreen = async () => {
    try {
      await document.documentElement.requestFullscreen()
      setFullscreenLost(false)
    } catch {
      toast.error('Could not re-enter fullscreen. Please try again.')
    }
  }

  // Copy/cut/paste disabled throughout the interview — stops pasting in a
  // pre-written answer or copying a question out to look it up elsewhere.
  useEffect(() => {
    if (stage !== 'in_progress') return
    const block = (e: ClipboardEvent) => {
      e.preventDefault()
      toast.error('Copy, cut, and paste are disabled during the interview.', { id: 'clipboard-blocked' })
    }
    document.addEventListener('copy', block)
    document.addEventListener('cut', block)
    document.addEventListener('paste', block)
    return () => {
      document.removeEventListener('copy', block)
      document.removeEventListener('cut', block)
      document.removeEventListener('paste', block)
    }
  }, [stage])

  // Best-effort deterrent against closing the tab — browsers only allow a
  // generic native confirmation, never a custom message or a true block.
  // If the candidate closes anyway, this also fires a best-effort save of
  // whatever they were typing/dictating/drawing on the current question —
  // without this, that answer was never sent to the server at all (saveDraft
  // only ever runs on navigating to another question or on final Submit), so
  // a forced tab-close silently lost it with no way to recover it on resume.
  useEffect(() => {
    if (stage !== 'in_progress') return
    const warnBeforeUnload = (e: BeforeUnloadEvent) => {
      const { token: tok, question, draft, timeSpent } = latestDraftRef.current
      if (tok && question && draft) {
        let answerText = draft.text
        let metadata = draft.metadata
        // Mirrors saveDraft's whiteboard handling — without this, a sketch
        // that was never explicitly saved (candidate drew, never navigated
        // away or submitted) had nothing to send here at all, since
        // whiteboard_image_data is normally only populated inside saveDraft.
        if (question.response_mode === 'whiteboard' && whiteboardRef.current && !whiteboardRef.current.isEmpty()) {
          metadata = { ...metadata, whiteboard_image_data: whiteboardRef.current.getSnapshot() }
          answerText = answerText || '[Design submitted as a whiteboard sketch]'
        }
        // latestDraftRef's timeSpent is only what accumulateElapsedTime()
        // has folded in from PRIOR visits to this question — it never
        // includes time elapsed on the CURRENT visit, since that only gets
        // folded in on the next navigation/submit, neither of which happens
        // here. Add the live delta directly so a candidate who opens a
        // question and closes the tab without ever navigating away doesn't
        // get "0 seconds" recorded despite real time spent.
        const { id: enteredId, at: enteredAt } = questionEnteredAtRef.current
        const liveElapsed = enteredId === question.id && enteredAt
          ? Math.max(0, Math.round((Date.now() - enteredAt) / 1000))
          : 0
        if (answerText.trim() || metadata.whiteboard_image_data || draft.markedForReview) {
          submitAnswerBeacon(tok, question.id, {
            answer_text: answerText,
            answer_metadata: { ...metadata, marked_for_review: draft.markedForReview },
            time_spent_seconds: timeSpent + liveElapsed,
          })
        }
      }
      e.preventDefault()
      e.returnValue = ''
    }
    // Leaving the page ends the attempt. Every question is visible from the
    // start, so a candidate who could close the tab, look answers up and
    // resume would have defeated the timing entirely — see the /abandon
    // endpoint. Ordered AFTER the draft save above so whatever they had
    // typed still reaches the server and shows on the partial scorecard.
    //
    // `pagehide` rather than `beforeunload`: it fires reliably on mobile and
    // on tab close, where beforeunload frequently does not.
    const abandonOnLeave = () => {
      const tok = latestDraftRef.current.token
      if (tok) abandonInterviewBeacon(tok, 'closed_tab')
    }
    window.addEventListener('beforeunload', warnBeforeUnload)
    window.addEventListener('pagehide', abandonOnLeave)
    return () => {
      window.removeEventListener('beforeunload', warnBeforeUnload)
      window.removeEventListener('pagehide', abandonOnLeave)
    }
  }, [stage])

  useEffect(() => {
    if (!token) return
    getVerifyScreen(token)
      .then((data) => {
        setScreen(data)
        setStage(
          data.status === 'completed' ? 'done'
            : data.status === 'incomplete' ? 'interrupted'
            : 'verify'
        )
      })
      .catch((err: any) => {
        // There is no expiry concept on this link at all - it never times out on its own - so a
        // 404 here means only one thing: no session exists for this token (wrong/mistyped link,
        // or a link copied for one environment and opened against another). Reporting that
        // honestly instead of a generic "invalid or has expired" avoids implying a timeout that
        // was never the actual cause. Anything other than 404 (network blip, 5xx) gets its own
        // distinct, retry-oriented message instead of being lumped in with "no such link".
        setErrorMessage(
          err?.response?.status === 404
            ? 'No interview found for this link. Please check the link and try again.'
            : 'Something went wrong loading this interview. Please try again in a moment.'
        )
        setStage('error')
      })
  }, [token])

  const handleConfirmAndContinue = async () => {
    if (!token) return
    try {
      setStarting(true)
      await verifyCandidate(token, true)
      setStage('precheck')
    } catch {
      toast.error('Something went wrong. Please try again.')
    } finally {
      setStarting(false)
    }
  }

  // The display check must have actually run and come back clean — 'unchecked'
  // no longer counts, so a candidate can't skip it. 'unsupported' still
  // passes: there is genuinely no equivalent API to run in that browser.
  // Screen+audio recording must also have actually started — same idea as
  // the display check, this is a real prerequisite, not just a checkbox.
  const canStartInterview =
    !!mediaStream &&
    recorder.isRecording &&
    integrityMonitor.modelsLoaded &&
    (monitorCheck === 'single' || monitorCheck === 'unsupported')

  // Runs from the Tips for Exam screen — the real backend "start" (timer
  // begins, questions are fetched here). ID Verification and Tips both
  // happen before this, purely client-side.
  const handleBeginInterview = async () => {
    if (!token || !canStartInterview) return
    // Must be requested synchronously within this click handler — browsers
    // only grant requestFullscreen() during a real user gesture.
    try {
      await document.documentElement.requestFullscreen()
    } catch {
      toast.error('Please allow fullscreen to begin the interview.')
      return
    }
    setStarting(true)
    try {
      const state = await startInterview(token, {
        browser_geo: geo,
        // canStartInterview above already blocks starting when 'multiple' was
        // detected, so this is always false here — recorded anyway for the
        // audit trail rather than hardcoded, in case that guard ever changes.
        extra_monitor_detected: (monitorCheck as MonitorCheckState) === 'multiple',
        declaration_confirmed: true,
        camera_mic_verified: true, // canStartInterview already required a live mediaStream
        camera_snapshot: idPhotoSnapshot,
      })
      setQuestions(state.questions)
      if (state.recording_chunk_interval_seconds) {
        setRecordingChunkIntervalMs(state.recording_chunk_interval_seconds * 1000)
      }
      // Resume support: reopening an already-started interview restores
      // prior answers/mark-for-review and the integrity-event history
      // instead of the UI looking reset (nothing was ever actually lost —
      // it's all been on the server the whole time).
      setAnswers(
        Object.fromEntries(
          Object.entries(state.answers || {}).map(([questionId, a]) => [
            questionId,
            { text: a.answer_text || '', metadata: a.answer_metadata || {}, markedForReview: a.marked_for_review },
          ])
        )
      )
      setIntegrityLog(
        (state.integrity_events || [])
          .map((e) => ({ type: e.type as IntegrityEventType, at: new Date(e.detected_at).getTime() }))
          .reverse() // server returns oldest-first; the panel shows most-recent-first
      )
      const startIndex = state.current_question_index || 0
      setQuestionIndex(startIndex)
      questionEnteredAtRef.current = { id: state.questions[startIndex]?.id || null, at: Date.now() }
      setStage('in_progress')
      setTourStep(0)
    } catch {
      toast.error('Something went wrong starting the interview. Please try again.')
    } finally {
      setStarting(false)
    }
  }

  const currentQuestion = questions[questionIndex]

  // Runs every render (no dependency array) purely to keep latestDraftRef
  // current for the beforeunload handler above — see its comment.
  useEffect(() => {
    latestDraftRef.current = {
      token: token || null,
      question: currentQuestion || null,
      draft: currentQuestion ? currentDraft(currentQuestion.id) : null,
      timeSpent: currentQuestion ? (timeSpentRef.current[currentQuestion.id] || 0) : 0,
    }
  })

  // The whiteboard is a fresh canvas each time its question is shown (keyed by
  // question.id), so coming back to a design question would show an empty
  // canvas — and anything drawn on it would replace the saved sketch. Put the
  // saved sketch back on the canvas instead, same as VMP's
  // loadWhiteboardSnapshotIfNeeded. Effects run after the canvas has mounted,
  // so whiteboardRef is already set here.
  const currentQuestionId = currentQuestion ? currentQuestion.id : null
  useEffect(() => {
    if (stage !== 'in_progress' || !currentQuestion || currentQuestion.response_mode !== 'whiteboard') return
    const draft = answersRef.current[currentQuestion.id]
    const snapshot = draft && draft.metadata ? draft.metadata.whiteboard_image_data : null
    if (snapshot && whiteboardRef.current) whiteboardRef.current.loadSnapshot(snapshot)
  }, [stage, currentQuestionId])

  // Adds elapsed time on the question the candidate is currently leaving to
  // its running total — must run before switching questionEnteredAtRef to
  // the next question.
  const accumulateElapsedTime = () => {
    const { id, at } = questionEnteredAtRef.current
    if (!id || !at) return
    const delta = Math.max(0, Math.round((Date.now() - at) / 1000))
    timeSpentRef.current[id] = (timeSpentRef.current[id] || 0) + delta
  }

  // Saves one question's answer — whatever the candidate has entered, with no
  // explicit "save" needed from them (verbal answers are the dictated text,
  // which already includes words still being spoken; coding is the editor
  // contents; design is the whiteboard sketch). Remembers what was last saved
  // per question so unchanged answers aren't re-sent, and retries a failed
  // save so a network blip doesn't silently lose an answer. Returns false only
  // if the answer still couldn't be saved.
  //
  // Reads drafts from answersRef (not state) because it's also called from
  // timers and proctoring handlers that hold an older render's closure.
  const saveQuestion = async (question: PublicQuestion): Promise<boolean> => {
    if (!token) return true
    const draft = answersRef.current[question.id] || { text: '', metadata: {}, markedForReview: false }
    let finalAnswerText = draft.text
    let metadata = draft.metadata

    // Only the question on screen has a live whiteboard canvas to read.
    const isOnScreen = questionsRef.current[questionIndexRef.current] === question
    if (isOnScreen && question.response_mode === 'whiteboard' && whiteboardRef.current && !whiteboardRef.current.isEmpty()) {
      metadata = { ...metadata, whiteboard_image_data: whiteboardRef.current.getSnapshot() }
      finalAnswerText = finalAnswerText || '[Design submitted as a whiteboard sketch]'
      updateDraft(question.id, { text: finalAnswerText, metadata })
    }

    // Still worth saving an otherwise-empty question if it's been marked
    // for review — that flag shouldn't need real answer content to stick.
    if (!finalAnswerText.trim() && !metadata.whiteboard_image_data && !draft.markedForReview) return true

    const payload = {
      answer_text: finalAnswerText,
      answer_metadata: { ...metadata, marked_for_review: draft.markedForReview },
      time_spent_seconds: timeSpentRef.current[question.id] || 0,
    }
    const signature = JSON.stringify([payload.answer_text, payload.answer_metadata])
    if (savedSignatureRef.current[question.id] === signature) return true

    for (let attempt = 0; attempt < ANSWER_SAVE_ATTEMPTS; attempt += 1) {
      try {
        await submitAnswer(token, question.id, payload)
        savedSignatureRef.current[question.id] = signature
        return true
      } catch {
        if (attempt < ANSWER_SAVE_ATTEMPTS - 1) await new Promise((r) => setTimeout(r, 1000 * (attempt + 1)))
      }
    }
    return false
  }

  // Saves every question with changes not yet saved (including ones whose
  // earlier save failed). Used on Submit, when time runs out, before a
  // proctoring strike is reported (a 3rd strike ends the test server-side,
  // after which answers are rejected), and before an attempt is ended for
  // being away.
  const saveAllUnsaved = async (): Promise<boolean> => {
    let allSaved = true
    for (const question of questionsRef.current) {
      if (!(await saveQuestion(question))) allSaved = false
    }
    return allSaved
  }
  // Latest version for handlers registered in effects (see saveQuestion).
  saveAllUnsavedRef.current = saveAllUnsaved

  // Autosave for whichever question is being left — fire-and-forget so
  // navigation feels instant. A failed save is retried by the next
  // saveAllUnsaved (Submit, time-up, or a proctoring event).
  const saveDraft = (question: PublicQuestion) => saveQuestion(question)

  const goToQuestion = (index: number) => {
    if (index === questionIndex || index < 0 || index >= questions.length || !currentQuestion) return
    accumulateElapsedTime()
    saveDraft(currentQuestion)
    setQuestionIndex(index)
    questionEnteredAtRef.current = { id: questions[index].id, at: Date.now() }
  }

  const toggleMarkForReview = (questionId: string) => {
    updateDraft(questionId, { markedForReview: !currentDraft(questionId).markedForReview })
  }

  const handleSubmitInterview = async () => {
    if (!token || !currentQuestion) return
    if (!window.confirm('Are you sure you want to submit the interview? You won\'t be able to make any changes after this.')) return
    accumulateElapsedTime()
    setSubmittingAnswer(true)
    try {
      // Every answer, not just the one on screen — an earlier save that
      // failed while moving between questions is retried here. If any still
      // won't save, don't submit: the server rejects answers once the
      // interview is completed, so submitting now would lose them for good.
      const allSaved = await saveAllUnsaved()
      if (!allSaved) {
        toast.error('Some answers could not be saved. Check your connection and click Submit again.')
        return
      }
      setStage('submitting')
      await completeInterview(token)
      await finishRecordingUpload()
      setStage('done')
    } catch {
      toast.error('Could not submit your interview. Please try again.')
      setStage('in_progress')
    } finally {
      setSubmittingAnswer(false)
    }
  }

  // "Next Question"/time-up advances, and goToQuestion saves the answer being
  // left. On the last question there's nowhere to advance to: a manual click
  // prompts to use Submit, and time running out saves the answer right away
  // (there's no move to trigger the save otherwise).
  const handleNext = (auto = false) => {
    if (!currentQuestion) return
    if (questionIndex + 1 >= questions.length) {
      if (auto) {
        const questionId = currentQuestion.id
        saveAllUnsaved().then((saved) => {
          // The timer can report time-up again (e.g. after a fullscreen
          // pause), so only tell the candidate once per question.
          if (saved && !timeUpNotifiedRef.current.has(questionId)) {
            timeUpNotifiedRef.current.add(questionId)
            toast('Time\'s up for this question — your answer has been saved.', { icon: '⏱️' })
          }
        })
      } else {
        toast('This is the last question — use Submit Interview when you\'re ready to finish.', { icon: 'ℹ️' })
      }
      return
    }
    goToQuestion(questionIndex + 1)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  if (stage === 'loading') {
    return (
      <PageShell>
        <CenteredShell><LoadingSpinner size="lg" /></CenteredShell>
      </PageShell>
    )
  }

  if (stage === 'error') {
    return (
      <PageShell>
        <CenteredShell>
          <div className="w-14 h-14 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-4">
            <ShieldCheckIcon className="w-7 h-7 text-red-400" />
          </div>
          <p className="text-gray-700">{errorMessage}</p>
        </CenteredShell>
      </PageShell>
    )
  }

  // 'submitting' and 'done' share ONE two-column screen: left is the
  // submission status (spinner while uploading, then the Thank You
  // confirmation once completeInterview()/finishRecordingUpload() resolve),
  // right is the feedback form — shown from the INSTANT Submit is clicked,
  // not held back until 'done'. The candidate can rate/tag/comment on their
  // experience while the recording upload is still in flight, rather than
  // waiting for a submission that can itself take a moment.
  if (stage === 'submitting' || stage === 'done') {
    return (
      <PageShell>
        <CenteredShell xl>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="flex flex-col items-center md:items-start text-center md:text-left justify-center">
              {stage === 'submitting' ? (
                <>
                  {/* Layered badge: a soft gradient disc, a static track ring,
                      a spinning accent ring on top of it, and a gently
                      pulsing upload icon in the center - reads as "actively
                      working", not just a bare spinner. */}
                  <div className="relative w-28 h-28 mb-7 flex-shrink-0">
                    <div className="absolute inset-0 rounded-full bg-gradient-to-br from-blue-50 to-indigo-100" />
                    <div className="absolute inset-2 rounded-full border-4 border-blue-100" />
                    <div className="absolute inset-2 rounded-full border-4 border-transparent border-t-blue-600 border-r-blue-600 animate-spin" />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <CloudArrowUpIcon className="w-12 h-12 text-blue-600 animate-pulse-slow" />
                    </div>
                  </div>
                  <h1 className="text-3xl font-bold text-gray-900 mb-3 tracking-tight">Submitting your interview…</h1>
                  <p className="text-base text-gray-500 max-w-sm leading-relaxed mb-6">
                    Uploading your screen recording — this can take a moment, please don't close this tab.
                  </p>
                  <div className="relative w-full max-w-xs h-2 rounded-full bg-gray-100 overflow-hidden">
                    <div className="absolute inset-y-0 left-0 rounded-full bg-gradient-to-r from-blue-500 to-indigo-500 animate-indeterminate-bar" />
                  </div>
                </>
              ) : (
                <>
                  <div className="relative w-28 h-28 mb-7 flex-shrink-0 animate-scale-in">
                    <div className="absolute inset-0 rounded-full bg-gradient-to-br from-green-50 to-emerald-100" />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <CheckCircleIcon className="w-14 h-14 text-green-500" />
                    </div>
                  </div>
                  <h1 className="text-3xl font-bold text-gray-900 mb-3 tracking-tight">Thank You!</h1>
                  <p className="text-base text-gray-500 max-w-sm leading-relaxed">
                    Your interview has been submitted successfully. The hiring team will review your responses and follow up
                    with next steps.
                  </p>
                </>
              )}
            </div>
            <div className="mt-8 pt-6 border-t border-gray-100 md:mt-0 md:pt-0 md:border-t-0 md:border-l md:pl-8 md:border-gray-100">
              {token && <FeedbackForm token={token} />}
            </div>
          </div>
        </CenteredShell>
      </PageShell>
    )
  }

  if (stage === 'verify' && screen) {
    return (
      <PageShell>
        <CenteredShell xl>
          <ScreenHeader
            icon={ShieldCheckIcon}
            title="Talent OS Proctored Assessments"
            subtitle="Please confirm your details below before you begin."
          />

          <div className="bg-gray-50 rounded-xl border border-gray-100 divide-y divide-gray-100 mb-6">
            <DetailRow icon={UserCircleIcon} label="Candidate" value={screen.candidate_name || '—'} />
            <DetailRow icon={BriefcaseIcon} label="Position" value={screen.job_title || '—'} />
            {/* Company / Questions / Estimated duration hidden for now — kept for a possible future toggle. */}
            {/* {screen.company && <DetailRow icon={BuildingOffice2Icon} label="Company" value={screen.company} />} */}
            {/* <DetailRow icon={ChatBubbleLeftRightIcon} label="Questions" value={String(screen.question_count)} /> */}
            {/* <DetailRow icon={ClockIcon} label="Estimated duration" value={`${screen.total_duration_minutes || '—'} minutes`} /> */}
          </div>

          <div className="mb-5">
            <p className="text-sm font-semibold text-gray-900 mb-1.5">Consent to AI-Assisted Proctoring</p>
            <p className="text-xs text-gray-500 leading-relaxed mb-2">
              This assessment uses TalentOS's AI-assisted proctoring to help ensure a fair and secure testing
              experience. By proceeding, you acknowledge and agree that:
            </p>
            <ul className="text-xs text-gray-500 leading-relaxed mb-2 list-disc pl-4 space-y-1">
              <li>Your camera, microphone, and screen will be recorded for the full duration of the assessment.</li>
              <li>
                TalentOS will perform automated identity verification (including a photo captured at the start of
                the session) and continuous integrity monitoring, such as presence detection, gaze and attention
                tracking, and device or browser activity checks.
              </li>
              <li>
                This data, along with your responses and results, will be analyzed by TalentOS's AI systems and
                shared only with the hiring team for the role you applied to.
              </li>
              <li>
                Your data will be handled as described in TalentOS's{' '}
                <LegalLink onOpen={() => setOpenLegalDoc('terms')}>Terms and Conditions</LegalLink> and{' '}
                <LegalLink onOpen={() => setOpenLegalDoc('privacy')}>Privacy Policy</LegalLink>, which you should
                read before continuing.
              </li>
            </ul>
            <p className="text-xs text-gray-500 leading-relaxed mb-3">
              If you do not consent, you will not be able to proceed with this assessment.
            </p>
            <ChecklistItem
              checked={policyAgreed}
              onChange={setPolicyAgreed}
              label={
                <>
                  I have read and agree to the{' '}
                  <LegalLink onOpen={() => setOpenLegalDoc('terms')}>Terms and Conditions</LegalLink> and{' '}
                  <LegalLink onOpen={() => setOpenLegalDoc('privacy')}>Privacy Policy</LegalLink>, and I consent to
                  the recording and AI-assisted proctoring described above.
                </>
              }
            />
          </div>
          <button
            onClick={handleConfirmAndContinue}
            disabled={starting || !policyAgreed}
            className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 text-white rounded-xl text-sm font-semibold hover:bg-blue-700 disabled:opacity-40 transition-colors shadow-sm shadow-blue-200"
          >
            {starting ? <LoadingSpinner size="sm" /> : null}
            {starting ? 'Continuing…' : 'Continue'}
          </button>

          <LegalDocumentModal
            open={openLegalDoc !== null}
            title={openLegalDoc === 'privacy' ? 'Privacy Policy' : 'Terms and Conditions'}
            content={openLegalDoc === 'privacy' ? PRIVACY_POLICY_MD : TERMS_OF_SERVICE_MD}
            onClose={() => setOpenLegalDoc(null)}
          />
        </CenteredShell>
      </PageShell>
    )
  }

  if (stage === 'interrupted' && screen) {
    const contact = screen.support_contact
    return (
      <PageShell>
        <CenteredShell>
          <div className="w-14 h-14 rounded-full bg-amber-50 flex items-center justify-center mb-5 mx-auto">
            <ExclamationTriangleIcon className="w-7 h-7 text-amber-600" />
          </div>
          <h1 className="text-xl font-semibold text-gray-900 mb-2">Assessment Interrupted</h1>
          <p className="text-sm text-gray-500 mb-6">
            Your assessment ended unexpectedly and has been closed. Everything you completed before
            that point has been saved. Please get in touch with the team below to arrange a new link.
          </p>

          {/* Rendered only when the organisation configured at least one
              channel — an empty contact card would be worse than none. */}
          {contact && (contact.phone || contact.email) && (
            <div className="bg-gray-50 rounded-xl border border-gray-100 divide-y divide-gray-100 mb-6 text-left">
              {contact.phone && (
                <a href={`tel:${contact.phone}`} className="flex items-center gap-2.5 px-4 py-3 hover:bg-gray-100 transition-colors">
                  <PhoneIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  <span className="text-sm text-gray-900 font-medium">{contact.phone}</span>
                </a>
              )}
              {contact.email && (
                <a href={`mailto:${contact.email}`} className="flex items-center gap-2.5 px-4 py-3 hover:bg-gray-100 transition-colors">
                  <EnvelopeIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  <span className="text-sm text-gray-900 font-medium break-all">{contact.email}</span>
                </a>
              )}
            </div>
          )}

          <p className="text-xs text-gray-400">
            This link can no longer be used to continue the assessment.
          </p>
        </CenteredShell>
      </PageShell>
    )
  }

  if (stage === 'precheck') {
    return (
      <PageShell>
        <CenteredShell xl>
          <ScreenHeader
            icon={ShieldCheckIcon}
            title="Before You Begin"
            subtitle="This interview is proctored. Please complete the checks below before starting."
          />

          {/* Two across on the wider card: three stacked rows plus the status
              circles below them was the main source of scrolling here. */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6 items-start">
            <div className="flex items-start gap-3 p-3.5 rounded-xl border border-gray-100 bg-gray-50">
              <div className="w-9 h-9 rounded-lg bg-white border border-gray-200 flex items-center justify-center flex-shrink-0">
                <VideoCameraIcon className="w-4 h-4 text-gray-500" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900">Camera &amp; microphone access</p>
                {mediaStream ? (
                  <p className="text-xs text-green-600 mt-0.5 flex items-center gap-1"><CheckCircleIcon className="w-3.5 h-3.5" /> Verified</p>
                ) : mediaError ? (
                  <p className="text-xs text-red-500 mt-0.5">{mediaError}</p>
                ) : (
                  <p className="text-xs text-gray-400 mt-0.5">Waiting for permission…</p>
                )}
              </div>
              {mediaStream && (
                <video
                  autoPlay muted playsInline
                  ref={precheckVideoRef}
                  className="w-16 h-12 rounded-md object-cover bg-black flex-shrink-0"
                />
              )}
            </div>

            <div className="flex items-start gap-3 p-3.5 rounded-xl border border-gray-100 bg-gray-50">
              <div className="w-9 h-9 rounded-lg bg-white border border-gray-200 flex items-center justify-center flex-shrink-0">
                <ComputerDesktopIcon className="w-4 h-4 text-gray-500" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900">Single display check</p>
                {monitorCheck === 'unchecked' && <p className="text-xs text-gray-400 mt-0.5">Checking…</p>}
                {monitorCheck === 'single' && <p className="text-xs text-green-600 mt-0.5 flex items-center gap-1"><CheckCircleIcon className="w-3.5 h-3.5" /> Verified — only one display detected</p>}
                {monitorCheck === 'multiple' && <p className="text-xs text-red-500 mt-0.5">Extra display detected — please disconnect it, then check again.</p>}
                {monitorCheck === 'unsupported' && <p className="text-xs text-gray-400 mt-0.5">Automatic detection isn't available in this browser.</p>}
              </div>
              {/* Only offered when an extra display was actually found — that
                  is the one state where the candidate has something to do
                  (disconnect it) and a reason to re-check. Everything else
                  resolves on its own when the screen loads. */}
              {monitorCheck === 'multiple' && (
                <button
                  onClick={handleCheckDisplays}
                  className="text-xs font-medium px-3 py-1.5 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-100 flex-shrink-0"
                >
                  Check again
                </button>
              )}
            </div>

            <div className="flex items-start gap-3 p-3.5 rounded-xl border border-gray-100 bg-gray-50">
              <div className="w-9 h-9 rounded-lg bg-white border border-gray-200 flex items-center justify-center flex-shrink-0">
                <ShieldExclamationIcon className="w-4 h-4 text-gray-500" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900">Screen &amp; audio recording</p>
                {recorder.isRecording ? (
                  <p className="text-xs text-green-600 mt-0.5 flex items-center gap-1"><CheckCircleIcon className="w-3.5 h-3.5" /> Recording — your entire screen and audio</p>
                ) : recorder.error ? (
                  <p className="text-xs text-red-500 mt-0.5">{recorder.error.message}</p>
                ) : (
                  <p className="text-xs text-gray-400 mt-0.5">Required — share your entire screen and allow audio before you can start.</p>
                )}
              </div>
              {!recorder.isRecording && (
                <button
                  onClick={() => recorder.startRecording().catch(() => {})}
                  disabled={!recorder.isSupported}
                  className="text-xs font-medium px-3 py-1.5 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-100 flex-shrink-0 disabled:opacity-40"
                >
                  Share Screen
                </button>
              )}
            </div>
          </div>

          <div className="flex items-start justify-center gap-6 sm:gap-10 mb-6">
            <PrecheckCircle
              icon={VideoCameraIcon}
              imageSrc="/images/precheck/camera.svg"
              label="Camera"
              verified={!!mediaStream}
              hasError={!!mediaError}
              statusText={mediaStream ? 'Verified' : mediaError ? mediaError : 'Waiting for permission…'}
            />
            <PrecheckCircle
              icon={MicrophoneIcon}
              imageSrc="/images/precheck/microphone.svg"
              label="Microphone"
              verified={!!mediaStream}
              hasError={!!mediaError}
              statusText={mediaStream ? 'Verified' : mediaError ? mediaError : 'Waiting for permission…'}
            />
            <PrecheckCircle
              icon={ComputerDesktopIcon}
              imageSrc="/images/precheck/monitor.svg"
              label="Monitor"
              verified={monitorCheck === 'single' || monitorCheck === 'unsupported'}
              hasError={monitorCheck === 'multiple'}
              statusText={
                monitorCheck === 'unchecked' ? 'Click Check to verify'
                  : monitorCheck === 'single' ? 'Verified — single display'
                  : monitorCheck === 'multiple' ? 'Extra display detected'
                  : "Not available in this browser"
              }
            />
          </div>

          <button
            onClick={() => setStage('id_verification')}
            disabled={!canStartInterview}
            className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 text-white rounded-xl text-sm font-semibold hover:bg-blue-700 disabled:opacity-40 transition-colors shadow-sm shadow-blue-200"
          >
            {!integrityMonitor.modelsLoaded ? <LoadingSpinner size="sm" /> : null}
            {!integrityMonitor.modelsLoaded ? 'Preparing interview…' : 'Continue'}
          </button>
        </CenteredShell>
      </PageShell>
    )
  }

  if (stage === 'id_verification') {
    return (
      <PageShell>
        <CenteredShell xl>
          <ScreenHeader
            icon={ShieldCheckIcon}
            title="ID Verification"
            subtitle="Hold your ID card up next to your face so we can capture a photo for the hiring team's records."
          />

          <div className="grid grid-cols-1 sm:grid-cols-5 gap-6 mb-6">
            <div className="sm:col-span-2">
              <p className="text-sm font-semibold text-gray-900 mb-2">Instructions</p>
              <ul className="text-xs text-gray-500 leading-relaxed list-disc pl-4 space-y-1.5">
                <li>Have your ID card ready</li>
                <li>Make sure your face and your ID are both clearly visible</li>
                <li>Hold your ID next to your face, not covering it</li>
                <li>Click Capture when you're ready</li>
              </ul>
              <p className="text-xs text-gray-400 mt-4">
                This photo is saved with your interview for the hiring team's identity check — it is not analyzed
                automatically, and no one is watching live.
              </p>
            </div>
            <div className="sm:col-span-3">
              {idPhotoSnapshot ? (
                <img
                  src={idPhotoSnapshot}
                  alt="Captured ID verification photo"
                  className="w-full aspect-video object-cover rounded-xl border border-gray-200 bg-black"
                />
              ) : (
                <>
                  <video
                    autoPlay muted playsInline
                    ref={idVerificationVideoRef}
                    className="w-full aspect-video object-cover rounded-xl border border-gray-200 bg-black"
                  />
                  <p className={`text-xs font-medium mt-2 flex items-center gap-1.5 ${
                    idCaptureGuide.status === 'ready' ? 'text-green-600'
                      : idCaptureGuide.status === 'loading' || idCaptureGuide.status === 'unavailable' ? 'text-gray-400'
                      : 'text-amber-600'
                  }`}>
                    {idCaptureGuide.status === 'ready' && <CheckCircleIcon className="w-3.5 h-3.5" />}
                    {idCaptureGuide.message}
                  </p>
                </>
              )}
            </div>
          </div>

          {idPhotoSnapshot && (
            <div className="mb-5 flex items-center gap-1.5 text-sm text-green-600">
              <CheckCircleIcon className="w-5 h-5" /> ID photo captured
            </div>
          )}

          <div className="flex gap-3">
            {idPhotoSnapshot && (
              <button
                onClick={() => setIdPhotoSnapshot(null)}
                className="flex-1 px-4 py-3 border border-gray-300 text-gray-600 rounded-xl text-sm font-semibold hover:bg-gray-50 transition-colors"
              >
                Retake
              </button>
            )}
            <button
              onClick={() => {
                if (!idPhotoSnapshot) {
                  // Re-checked at the moment of click, not just shown live above —
                  // a candidate can click faster than the ~500ms detection tick.
                  // 'unavailable' (models failed to load) falls back to trusting
                  // manual capture rather than blocking it forever.
                  if (idCaptureGuide.status !== 'ready' && idCaptureGuide.status !== 'unavailable') {
                    toast.error(idCaptureGuide.message)
                    return
                  }
                  const snap = captureSnapshotFromVideo(idVerificationVideoRef.current)
                  if (snap) setIdPhotoSnapshot(snap)
                  else toast.error('Could not capture a photo — please try again.')
                } else {
                  setStage('exam_tips')
                }
              }}
              className="flex-1 inline-flex items-center justify-center px-4 py-3 bg-blue-600 text-white rounded-xl text-sm font-semibold hover:bg-blue-700 transition-colors shadow-sm shadow-blue-200"
            >
              {idPhotoSnapshot ? 'Continue' : 'Capture'}
            </button>
          </div>
        </CenteredShell>
      </PageShell>
    )
  }

  if (stage === 'exam_tips') {
    return (
      <PageShell>
        <CenteredShell xxl>
          {/* Not shown to the candidate — this screen has no camera box, but the
              camera is still live and this screen has no time limit, so integrity
              monitoring (see its `active` comment) needs a live element to read. */}
          <video autoPlay muted playsInline aria-hidden="true" ref={examTipsVideoRef} className="sr-only" />
          <ScreenHeader icon={InformationCircleIcon} title="Before you start" />

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-5">
            <div>
              <h2 className="text-sm font-semibold text-gray-900 mb-0.5">Tips for Exam</h2>
              <p className="text-xs text-gray-500 mb-2.5">A few things to keep in mind before you start.</p>
              <div className="grid grid-cols-2 gap-2.5">
                {EXAM_TIPS.map((tip) => (
                  <div key={tip.label} className="p-2.5 rounded-xl border border-gray-200 bg-white hover:shadow-md transition-shadow text-center">
                    <div className="w-full h-16 flex items-center justify-center mb-2">
                      {tip.imageSrc ? (
                        <img src={tip.imageSrc} alt="" className="w-full h-full object-contain" />
                      ) : (
                        <tip.icon className="w-8 h-8 text-blue-400" />
                      )}
                    </div>
                    <p className="text-[11px] text-gray-600 leading-snug">{tip.label}</p>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <h2 className="text-sm font-semibold text-gray-900 mb-0.5">Test Guidelines</h2>
              <p className="text-xs text-gray-500 mb-2.5">
                Your screen, camera, and microphone are recorded for the entire session. The following are tracked
                and recorded as violations — 3 violations will automatically submit your interview.
              </p>
              <div className="grid grid-cols-1 gap-2">
                {TEST_GUIDELINES.map((rule) => (
                  <div key={rule.label} className="flex items-center gap-2.5 p-2.5 rounded-xl border border-gray-100 bg-gray-50">
                    <div className="w-8 h-8 rounded-lg bg-white border border-gray-200 flex items-center justify-center flex-shrink-0">
                      <rule.icon className="w-4 h-4 text-red-500" />
                    </div>
                    <p className="text-[11px] text-gray-600 leading-snug">{rule.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <button
            onClick={handleBeginInterview}
            disabled={!canStartInterview || starting}
            className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 text-white rounded-xl text-sm font-semibold hover:bg-blue-700 disabled:opacity-40 transition-colors shadow-sm shadow-blue-200"
          >
            {starting ? <LoadingSpinner size="sm" /> : null}
            {starting ? 'Starting…' : 'Start Interview'}
          </button>
        </CenteredShell>
      </PageShell>
    )
  }

  if (stage === 'in_progress' && currentQuestion) {
    const draft = currentDraft(currentQuestion.id)
    return (
      <PageShell
        header={
          <InterviewHeader
            jobTitle={screen?.job_title}
            company={screen?.company}
            index={questionIndex}
            total={questions.length}
            onSubmit={handleSubmitInterview}
            submitting={submittingAnswer}
          />
        }
      >
        <div className="max-w-[1800px] mx-auto px-6 py-6 grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-6 items-start lg:min-h-[calc(100vh-140px)]">
          {/* The COLUMN is sticky, not its children. Previously the navigator
              and the camera preview were each `sticky top-20` individually,
              so on scroll they detached separately and pinned to the same
              offset — the camera slid up over the question tracker. Sticking
              the column keeps them in their normal relative positions.
              Capped to the viewport with its own scrollbar so a tall column
              can still be reached on short screens. */}
          <div className="space-y-4 lg:sticky lg:top-20 lg:max-h-[calc(100vh-6rem)] lg:overflow-y-auto">
            <QuestionNavigator
              questions={questions}
              currentIndex={questionIndex}
              answers={answers}
              onSelect={goToQuestion}
            />
            <CameraPreview stream={mediaStream} onVideoElReady={setCameraVideoEl} />
            {/* Integrity Events list hidden from the candidate for now — commented out, not deleted, so it's easy to bring back. */}
            {/* <IntegrityEventsPanel events={integrityLog} modelsLoaded={integrityMonitor.modelsLoaded} modelsError={integrityMonitor.modelsError} /> */}
          </div>

          <QuestionCard
            token={token!}
            question={currentQuestion}
            index={questionIndex}
            total={questions.length}
            answerText={draft.text}
            onChangeAnswer={(v) => updateDraft(currentQuestion.id, { text: v })}
            markedForReview={draft.markedForReview}
            onToggleMarkForReview={() => toggleMarkForReview(currentQuestion.id)}
            onNext={() => handleNext(false)}
            onTimeUp={() => handleNext(true)}
            onSubmitInterview={handleSubmitInterview}
            isLastQuestion={questionIndex + 1 >= questions.length}
            submitting={submittingAnswer}
            whiteboardRef={whiteboardRef}
            codeLanguage={draft.metadata.language || 'python3'}
            onCodeLanguageChange={(lang) => updateDraft(currentQuestion.id, { metadata: { ...draft.metadata, language: lang } })}
            paused={fullscreenLost}
            alreadySpentSeconds={timeSpentRef.current[currentQuestion.id] || 0}
          />
        </div>
        {fullscreenLost && (
          <FullscreenViolationOverlay
            violationCount={violationCount}
            graceSecondsLeft={graceSecondsLeft}
            reportFailed={violationReportFailed}
            violationType={lastViolationType}
            onReturn={handleReturnToFullscreen}
          />
        )}
        {tourStep !== null && !fullscreenLost && (
          <InterviewTour
            step={tourStep}
            onSkip={() => setTourStep(null)}
            onNext={() => setTourStep((s) => (s === null ? null : s + 1))}
            onFinish={() => setTourStep(null)}
          />
        )}
      </PageShell>
    )
  }

  return (
    <PageShell>
      <CenteredShell><LoadingSpinner size="lg" /></CenteredShell>
    </PageShell>
  )
}

// ============================================================================
// Shell / layout
// ============================================================================

// Quick-select feedback options. The `key` is what gets stored and must match
// FEEDBACK_TAGS in proctored_interview_public.py; the label is display-only,
// so rewording it here never invalidates feedback already collected.
const FEEDBACK_TAGS: { key: string; label: string }[] = [
  { key: 'instructions_clear', label: 'Instructions were clear' },
  { key: 'platform_easy', label: 'Platform was easy to use' },
  { key: 'questions_relevant', label: 'Questions were relevant to the role' },
  { key: 'enough_time', label: 'I had enough time to answer' },
  { key: 'technical_issues', label: 'I experienced technical issues' },
  { key: 'setup_difficult', label: 'Camera / microphone setup was difficult' },
]

/** Optional experience feedback on the thank-you screen.
 *
 * Deliberately after the confirmation message, never blocking it: the
 * candidate has finished, and the first thing they need is certainty that
 * their work was submitted. Skipping is the default — there is no dismissal
 * step, the form is simply left alone.
 */
function FeedbackForm({ token }: { token: string }) {
  const [rating, setRating] = useState(0)
  const [hovered, setHovered] = useState(0)
  const [tags, setTags] = useState<string[]>([])
  const [comment, setComment] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [sent, setSent] = useState(false)

  const toggleTag = (key: string) =>
    setTags((prev) => (prev.includes(key) ? prev.filter((t) => t !== key) : [...prev, key]))

  const submit = async () => {
    if (rating === 0) return
    setSubmitting(true)
    try {
      await submitFeedback(token, { rating, tags, comment })
      setSent(true)
    } catch {
      toast.error('Could not send your feedback. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  if (sent) {
    return (
      <div>
        <p className="text-sm text-gray-500">Thank you for the feedback — it helps us improve.</p>
      </div>
    )
  }

  return (
    <div className="text-left">
      <p className="text-sm font-medium text-gray-900 mb-1">How was your experience?</p>
      <p className="text-xs text-gray-400 mb-4">Optional — this is about the platform, not your answers.</p>

      <div className="flex items-center justify-start gap-1 mb-5">
        {[1, 2, 3, 4, 5].map((star) => {
          // Hover preview falls back to the committed rating, so moving the
          // cursor away doesn't make an already-chosen rating look unset.
          const filled = star <= (hovered || rating)
          return (
            <button
              key={star}
              type="button"
              onClick={() => setRating(star)}
              onMouseEnter={() => setHovered(star)}
              onMouseLeave={() => setHovered(0)}
              aria-label={`${star} star${star === 1 ? '' : 's'}`}
              className="p-0.5 transition-transform hover:scale-110"
            >
              {filled
                ? <StarIconSolid className="w-7 h-7 text-amber-400" />
                : <StarIcon className="w-7 h-7 text-gray-300" />}
            </button>
          )
        })}
      </div>

      <div className="space-y-2 mb-4">
        {FEEDBACK_TAGS.map((tag) => (
          <label key={tag.key} className="flex items-start gap-2.5 cursor-pointer group">
            <input
              type="checkbox"
              checked={tags.includes(tag.key)}
              onChange={() => toggleTag(tag.key)}
              className="mt-0.5 w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 flex-shrink-0"
            />
            <span className="text-sm text-gray-600 group-hover:text-gray-800">{tag.label}</span>
          </label>
        ))}
      </div>

      <textarea
        value={comment}
        onChange={(e) => setComment(e.target.value)}
        placeholder="Anything else you'd like us to know? (optional)"
        rows={3}
        maxLength={2000}
        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm resize-none focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100 mb-4"
      />

      <button
        onClick={submit}
        disabled={rating === 0 || submitting}
        className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-xl text-sm font-semibold hover:bg-blue-700 disabled:opacity-40 transition-colors"
      >
        {submitting ? <LoadingSpinner size="sm" /> : null}
        {submitting ? 'Sending…' : 'Send Feedback'}
      </button>
      {rating === 0 && (
        <p className="text-xs text-gray-400 text-center mt-2">Select a star rating to send.</p>
      )}
    </div>
  )
}

/** Header for the pre-assessment screens: icon to the LEFT of the text
 * rather than in a circle stacked above it.
 *
 * Beyond reading better, this reclaims roughly 76px of vertical space per
 * screen (a 56px circle plus its margin) — these screens were scrolling, and
 * a centred badge was spending height on decoration before a candidate saw
 * a single word.
 */
function ScreenHeader({
  icon: Icon, title, subtitle, tone = 'blue',
}: {
  icon: React.FC<{ className?: string }>
  title: string
  subtitle?: React.ReactNode
  tone?: 'blue' | 'amber'
}) {
  const toneClasses = tone === 'amber' ? 'bg-amber-50 text-amber-600' : 'bg-blue-50 text-blue-600'
  return (
    <div className="flex items-start gap-3.5 mb-6">
      <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${toneClasses}`}>
        <Icon className="w-6 h-6" />
      </div>
      <div className="min-w-0 pt-0.5">
        <h1 className="text-xl font-semibold text-gray-900">{title}</h1>
        {subtitle && <p className="text-sm text-gray-500 mt-0.5">{subtitle}</p>}
      </div>
    </div>
  )
}

function PageShell({ children, header }: { children: React.ReactNode; header?: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-gray-50">
      {header}
      {children}
    </div>
  )
}

function CenteredShell({ children, wide, xl, xxl }: { children: React.ReactNode; wide?: boolean; xl?: boolean; xxl?: boolean }) {
  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <div className={`bg-white rounded-2xl shadow-xl shadow-gray-200/60 border border-gray-100 p-8 ${
        xxl ? 'max-w-6xl w-full text-left' : xl ? 'max-w-4xl w-full text-left' : wide ? 'max-w-lg w-full text-left' : 'max-w-sm w-full text-center'
      }`}>
        {children}
      </div>
    </div>
  )
}

function DetailRow({ icon: Icon, label, value }: { icon: React.FC<{ className?: string }>; label: string; value: string }) {
  return (
    <div className="flex items-center justify-between px-4 py-3">
      <span className="flex items-center gap-2 text-sm text-gray-500">
        <Icon className="w-4 h-4 text-gray-400" /> {label}
      </span>
      <span className="text-sm text-gray-900 font-medium">{value}</span>
    </div>
  )
}

// Inline link that opens a legal document over the consent screen.
//
// Rendered as a <button>, not an <a>, because one instance sits inside the
// consent checkbox's <label>. Any click inside a <label> activates its
// control, so without preventDefault a candidate opening the Privacy Policy
// would silently tick their own consent box; stopPropagation keeps the click
// from reaching handlers further up as well.
function LegalLink({ onOpen, children }: { onOpen: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={(e) => {
        e.preventDefault()
        e.stopPropagation()
        onOpen()
      }}
      className="text-blue-600 hover:text-blue-700 underline font-medium"
    >
      {children}
    </button>
  )
}

// `label` is a ReactNode rather than a string so the consent copy can embed
// the Terms/Privacy links above.
function ChecklistItem({ checked, onChange, label }: { checked: boolean; onChange: (v: boolean) => void; label: React.ReactNode }) {
  return (
    <label className="flex items-start gap-2.5 cursor-pointer group">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-0.5 w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 flex-shrink-0"
      />
      <span className="text-sm text-gray-600 group-hover:text-gray-800">{label}</span>
    </label>
  )
}

// One of the 3 precheck circles (Camera / Microphone / Monitor) on the
// "Before You Begin" screen. Unverified: a dashed grey ring. Verified: the
// ring turns solid green and a checkmark badge lands on its edge — the same
// "ring fills in, badge marks it done" language as Proctortrack's circles,
// built from our own CSS/icons rather than their illustration artwork.
// `imageSrc` (a free-license illustration under /public/images/precheck) is
// shown inside the ring, falling back to the plain icon if the image is
// missing. `action` renders below the label (e.g. the Monitor circle's
// "Check" button).
function PrecheckCircle({
  icon: Icon, label, verified, hasError, statusText, imageSrc, action,
}: {
  icon: React.FC<{ className?: string }>
  label: string
  verified: boolean
  hasError: boolean
  statusText: string
  imageSrc?: string
  action?: React.ReactNode
}) {
  return (
    <div className="flex flex-col items-center text-center w-24">
      <div className="relative w-16 h-16">
        <div
          className={`w-16 h-16 rounded-full flex items-center justify-center overflow-hidden bg-gray-50 border-2 transition-colors ${
            verified ? 'border-green-500' : hasError ? 'border-red-400 border-solid' : 'border-gray-300 border-dashed'
          }`}
        >
          {imageSrc ? <img src={imageSrc} alt={label} className="w-full h-full object-contain p-1.5" /> : <Icon className="w-6 h-6 text-gray-400" />}
        </div>
        {verified && (
          <span className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full bg-green-500 ring-2 ring-white flex items-center justify-center">
            <CheckIconSolid className="w-3 h-3 text-white" />
          </span>
        )}
        {!verified && hasError && (
          <span className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full bg-red-500 ring-2 ring-white flex items-center justify-center">
            <ExclamationTriangleIconSolid className="w-3 h-3 text-white" />
          </span>
        )}
      </div>
      <p className="text-xs font-semibold text-gray-900 mt-2">{label}</p>
      <p className={`text-[11px] mt-0.5 leading-snug ${
        verified ? 'text-green-600' : hasError ? 'text-red-500' : 'text-gray-400'
      }`}>
        {statusText}
      </p>
      {action}
    </div>
  )
}

// Always-visible self-view during the interview, reusing the stream
// captured on the device-check screen so there's no re-prompt/flicker.
// `onVideoElReady` hands the live <video> element up to the page so
// useIntegrityMonitor can read frames from it.
//
// Uses a stable useRef + one effect keyed on [stream] — NOT an inline
// `ref={(el) => ...}` callback. An inline arrow function is a new function
// identity every render, and React detaches/reattaches a callback ref
// whenever its identity changes; with QuestionCard re-rendering this tree
// every second (its countdown timer) that was calling onVideoElReady(null)
// then onVideoElReady(el) dozens of times a minute, repeatedly tearing down
// srcObject and restarting useIntegrityMonitor's polling loop — the actual
// cause of the camera going black/frozen and detection barely running.
function CameraPreview({ stream, onVideoElReady }: { stream: MediaStream | null; onVideoElReady?: (el: HTMLVideoElement | null) => void }) {
  const videoRef = useRef<HTMLVideoElement>(null)

  useEffect(() => {
    if (videoRef.current) videoRef.current.srcObject = stream
    onVideoElReady?.(stream ? videoRef.current : null)
    return () => onVideoElReady?.(null)
  }, [stream])

  if (!stream) return null
  return (
    <div data-tour="camera-preview">
      <p className="text-xs font-semibold text-gray-500 mb-2">Your Camera</p>
      <div className="w-full aspect-video rounded-xl overflow-hidden shadow-lg border-2 border-white ring-1 ring-black/10 bg-black">
        <video ref={videoRef} autoPlay muted playsInline className="w-full h-full object-cover" />
      </div>
    </div>
  )
}

// Left sidebar — every question as a numbered, jumpable button. Answered
// (non-empty draft) turns green, unanswered stays white, and a question
// marked for review shows a small flag badge regardless of answered state.
function QuestionNavigator({
  questions, currentIndex, answers, onSelect,
}: { questions: PublicQuestion[]; currentIndex: number; answers: Record<string, AnswerDraft>; onSelect: (index: number) => void }) {
  const answeredCount = questions.filter((q) => {
    const d = answers[q.id]
    return !!(d?.text?.trim() || d?.metadata?.whiteboard_image_data)
  }).length

  return (
    <div data-tour="questions-panel" className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
      <p className="flex items-center gap-1.5 text-xs font-semibold text-gray-500 mb-1">
        Questions
        <Tooltip content="Jump to any question here — your answer is saved as you navigate." position="right" theme="light">
          <InformationCircleIcon className="w-3.5 h-3.5 text-gray-300 hover:text-gray-500 cursor-pointer" />
        </Tooltip>
      </p>
      <p className="text-xs text-gray-400 mb-3">{answeredCount} of {questions.length} answered</p>
      <div className="grid grid-cols-5 lg:grid-cols-4 gap-2">
        {questions.map((q, idx) => {
          const draft = answers[q.id]
          const answered = !!(draft?.text?.trim() || draft?.metadata?.whiteboard_image_data)
          const active = idx === currentIndex
          return (
            <button
              key={q.id}
              onClick={() => onSelect(idx)}
              title={draft?.markedForReview ? 'Marked for review' : undefined}
              className={`relative w-10 h-10 rounded-lg text-sm font-semibold border transition-colors ${
                active ? 'ring-2 ring-blue-500 ring-offset-1' : ''
              } ${answered ? 'bg-green-100 border-green-300 text-green-700 hover:bg-green-200' : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50'}`}
            >
              {idx + 1}
              {draft?.markedForReview && (
                <FlagIconSolid className="w-3.5 h-3.5 text-amber-500 absolute -top-1.5 -right-1.5 drop-shadow" />
              )}
            </button>
          )
        })}
      </div>
      <div className="flex items-center gap-3 mt-4 pt-3 border-t border-gray-100 text-[11px] text-gray-400">
        <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-green-100 border border-green-300" /> Answered</span>
        <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-white border border-gray-300" /> Unanswered</span>
      </div>
    </div>
  )
}

// Right sidebar — live feed of client-side camera/mic integrity detections
// (multiple persons, device in frame, looking away, camera blocked,
// multiple voices), most recent first.
function IntegrityEventsPanel({
  events, modelsLoaded, modelsError,
}: { events: IntegrityLogEntry[]; modelsLoaded: boolean; modelsError: string | null }) {
  return (
    <div data-tour="integrity-panel" className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
      <p className="text-xs font-semibold text-gray-500 mb-1 flex items-center gap-1.5">
        <ShieldExclamationIcon className="w-4 h-4 text-gray-400" /> Integrity Events
      </p>
      {modelsError && (
        <p className="text-xs text-amber-600 mt-2">Some detection checks couldn't start in this session — the rest still run. (See browser console for details.)</p>
      )}
      {!modelsLoaded ? (
        <p className="text-xs text-gray-400 mt-2 flex items-center gap-1.5"><LoadingSpinner size="sm" /> Starting detection…</p>
      ) : events.length === 0 ? (
        <p className="text-xs text-gray-400 mt-2">No issues detected.</p>
      ) : (
        <div className="mt-2 max-h-72 overflow-y-auto flex flex-col gap-2">
          {events.map((e, idx) => (
            <div key={idx} className="flex items-start gap-2 text-xs">
              <ExclamationTriangleIcon className="w-3.5 h-3.5 text-amber-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-gray-700 leading-tight">{INTEGRITY_EVENT_LABELS[e.type]}</p>
                <p className="text-gray-400">{new Date(e.at).toLocaleTimeString()}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function FullscreenViolationOverlay({
  violationCount, graceSecondsLeft, reportFailed, violationType, onReturn,
}: { violationCount: number; graceSecondsLeft: number | null; reportFailed: boolean; violationType: string; onReturn: () => void }) {
  const counting = graceSecondsLeft !== null && graceSecondsLeft > 0
  const isTabSwitch = violationType === 'tab_switch'
  // Countdown ring progress - drains from full (just exited) to empty (grace
  // period about to expire), read against the same limit the backend
  // actually enforces (see FULLSCREEN_ABSENCE_LIMIT_MS's own comment).
  const graceTotalSeconds = FULLSCREEN_ABSENCE_LIMIT_MS / 1000
  const ringProgress = counting ? (graceSecondsLeft as number) / graceTotalSeconds : 0
  const RING_RADIUS = 28
  const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS

  return (
    <div className="fixed inset-0 z-50 bg-gray-900/80 backdrop-blur-md flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-8 sm:p-9 text-center animate-scale-in">
        {/* Icon + an expanding "alert ping" ring behind it - reads as an
            active, time-sensitive warning rather than a static notice. */}
        <div className="relative w-24 h-24 mx-auto mb-6">
          <div className="absolute inset-0 rounded-full bg-red-100 animate-ping opacity-40" />
          <div className="absolute inset-0 rounded-full bg-red-50 flex items-center justify-center">
            <ExclamationTriangleIcon className="w-11 h-11 text-red-500" />
          </div>
        </div>

        <h2 className="text-2xl font-bold text-gray-900 mb-2 tracking-tight">
          {isTabSwitch ? 'You left the tab' : 'You exited fullscreen'}
        </h2>

        {counting ? (
          <>
            <p className="text-sm text-gray-500 mb-5">Return now to avoid a strike on your assessment.</p>
            <div className="flex items-center justify-center gap-4 mb-2">
              <div className="relative w-16 h-16 flex-shrink-0">
                <svg className="w-16 h-16 -rotate-90" viewBox="0 0 64 64">
                  <circle cx="32" cy="32" r={RING_RADIUS} fill="none" stroke="#fee2e2" strokeWidth="6" />
                  <circle
                    cx="32" cy="32" r={RING_RADIUS} fill="none" stroke="#dc2626" strokeWidth="6"
                    strokeLinecap="round"
                    strokeDasharray={RING_CIRCUMFERENCE}
                    strokeDashoffset={RING_CIRCUMFERENCE * (1 - ringProgress)}
                    className="transition-[stroke-dashoffset] duration-1000 ease-linear"
                  />
                </svg>
                <span className="absolute inset-0 flex items-center justify-center text-xl font-bold text-red-600 tabular-nums">
                  {graceSecondsLeft}
                </span>
              </div>
              <span className="text-sm font-semibold text-amber-600 text-left leading-tight">seconds<br />remaining</span>
            </div>
          </>
        ) : reportFailed ? (
          <p className="text-base font-medium text-red-600 mb-1">Couldn't record this — check your connection.</p>
        ) : null}

        {/* Violation dots - a strike-counter at a glance, alongside the exact count in the caption below. */}
        <div className="flex items-center justify-center gap-2 mt-5 mb-3">
          {[1, 2, 3].map((n) => (
            <span key={n} className={`w-2.5 h-2.5 rounded-full transition-colors ${n <= violationCount ? 'bg-red-500' : 'bg-gray-200'}`} />
          ))}
        </div>
        <p className="text-sm text-gray-400 mb-7">
          {!counting && !reportFailed ? `Recorded as violation ${violationCount} of 3. ` : ''}
          3 violations will auto-submit your interview.
        </p>

        <button
          onClick={onReturn}
          className="w-full inline-flex items-center justify-center gap-2 px-5 py-3.5 bg-blue-600 text-white rounded-2xl text-base font-semibold hover:bg-blue-700 active:scale-[0.98] transition-all shadow-lg shadow-blue-600/20"
        >
          <ArrowsPointingOutIcon className="w-5 h-5" />
          Return to the Interview
        </button>
      </div>
    </div>
  )
}

// ============================================================================
// Guided tour — shown once, right when the interview starts. Step 0 is a
// centered intro card (Skip / Take the tour); steps 1..TOUR_STEPS.length
// spotlight one element each via the `data-tour="..."` attributes placed on
// the real DOM nodes above. The spotlight is a single absolutely-positioned
// box with a giant box-shadow (a common no-library "hole in the overlay"
// trick) rather than an SVG mask, so no extra dependency is needed.
// ============================================================================

const TOUR_STEPS: { selector: string; title: string; description: string }[] = [
  { selector: '[data-tour="questions-panel"]', title: 'Questions Panel', description: "Jump to any question anytime — your answers are saved automatically as you navigate." },
  { selector: '[data-tour="question-counter"]', title: 'Track Your Progress', description: "Shows which question you're on and how many are left." },
  { selector: '[data-tour="flag-button"]', title: 'Flag for Review', description: 'Not sure about an answer? Flag it here and revisit it before submitting.' },
  { selector: '[data-tour="timer"]', title: 'Question Timer', description: 'Each question is timed — when it hits zero, your answer is auto-submitted and you move on.' },
  { selector: '[data-tour="camera-preview"]', title: 'Your Camera', description: 'Your camera stays on for verification throughout the interview.' },
  { selector: '[data-tour="next-question"]', title: 'Move Forward', description: "Click here when you're ready to continue." },
  // Last on purpose: the tour should end on the action that ends the
  // interview, not introduce it before the candidate has answered anything.
  { selector: '[data-tour="submit-assessment"]', title: 'Submit When Finished', description: "Submits your whole assessment from any question — you'll be asked to confirm first, and you can't make changes afterwards." },
]

function InterviewTour({
  step, onSkip, onNext, onFinish,
}: { step: number; onSkip: () => void; onNext: () => void; onFinish: () => void }) {
  const [rect, setRect] = useState<DOMRect | null>(null)
  const spotlightIndex = step - 1 // step 0 is the intro card, which has no target

  useEffect(() => {
    const current = TOUR_STEPS[spotlightIndex]
    if (!current) {
      setRect(null)
      return
    }
    const el = document.querySelector(current.selector)
    if (!el) {
      setRect(null)
      return
    }
    el.scrollIntoView({ behavior: 'smooth', block: 'center' })
    const measure = () => setRect(el.getBoundingClientRect())
    measure()
    // Re-measure a few times while the smooth scroll above is still animating.
    let ticks = 0
    const interval = setInterval(() => {
      measure()
      if (++ticks > 6) clearInterval(interval)
    }, 100)
    window.addEventListener('resize', measure)
    return () => {
      clearInterval(interval)
      window.removeEventListener('resize', measure)
    }
  }, [spotlightIndex])

  if (step === 0) {
    return (
      <div className="fixed inset-0 z-[70] bg-black/60 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl max-w-sm w-full p-6 text-center">
          <h2 className="text-lg font-semibold text-gray-900 mb-1.5">Want a quick tour?</h2>
          <p className="text-sm text-gray-500 mb-5">We'll walk you through the interview screen in a few seconds.</p>
          <div className="flex items-center gap-2">
            <button onClick={onSkip} className="flex-1 px-4 py-2.5 bg-white text-gray-600 border border-gray-300 rounded-xl text-sm font-semibold hover:bg-gray-50 transition-colors">
              Skip
            </button>
            <button onClick={onNext} className="flex-1 px-4 py-2.5 bg-blue-600 text-white rounded-xl text-sm font-semibold hover:bg-blue-700 transition-colors">
              Take the tour
            </button>
          </div>
        </div>
      </div>
    )
  }

  const current = TOUR_STEPS[spotlightIndex]
  if (!current || !rect) return null
  const isLast = spotlightIndex === TOUR_STEPS.length - 1

  const placeAbove = window.innerHeight - rect.bottom < 170 && rect.top > 170
  const left = Math.min(Math.max(12, rect.left), window.innerWidth - 300)

  return (
    <>
      {/* Blocks interaction with the page underneath while the tour is active */}
      <div className="fixed inset-0 z-[70]" />
      <div
        className="fixed z-[71] rounded-lg pointer-events-none transition-all duration-200"
        style={{
          top: rect.top - 4,
          left: rect.left - 4,
          width: rect.width + 8,
          height: rect.height + 8,
          boxShadow: '0 0 0 9999px rgba(0,0,0,0.6)',
          border: '2px solid #2563EB',
        }}
      />
      <div
        className="fixed z-[72] bg-white rounded-xl shadow-xl p-4 w-72 transition-all duration-200"
        style={placeAbove ? { bottom: window.innerHeight - rect.top + 12, left } : { top: rect.bottom + 12, left }}
      >
        <p className="text-xs font-medium text-blue-600 mb-1">Step {step} of {TOUR_STEPS.length}</p>
        <p className="text-sm font-semibold text-gray-900 mb-1">{current.title}</p>
        <p className="text-xs text-gray-500 mb-4 leading-relaxed">{current.description}</p>
        <div className="flex items-center justify-between">
          <button onClick={onSkip} className="text-xs font-medium text-gray-400 hover:text-gray-600">Skip tour</button>
          <button
            onClick={isLast ? onFinish : onNext}
            className="px-3.5 py-1.5 bg-blue-600 text-white rounded-lg text-xs font-semibold hover:bg-blue-700 transition-colors"
          >
            {isLast ? 'Got it' : 'Next'}
          </button>
        </div>
      </div>
    </>
  )
}

function InterviewHeader({
  jobTitle, company, index, total, onSubmit, submitting,
}: {
  jobTitle?: string | null
  company?: string | null
  index: number
  total: number
  // Reuses the same handler as the Submit button on the last question, so the
  // confirmation prompt and the recording-flush-before-finish behaviour are
  // shared rather than duplicated — this is only a second way to reach it.
  onSubmit: () => void
  submitting: boolean
}) {
  const progressPct = total > 0 ? ((index) / total) * 100 : 0
  return (
    <div className="sticky top-0 z-10 bg-white/90 backdrop-blur border-b border-gray-200">
      <div className="max-w-[1800px] mx-auto px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2 min-w-0">
          <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
            <ShieldCheckIcon className="w-4 h-4 text-blue-600" />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-medium text-gray-900 truncate">{jobTitle || 'Proctored Assessment'}</p>
            {company && <p className="text-xs text-gray-400 truncate">{company}</p>}
          </div>
        </div>
        <div className="flex items-center gap-4 flex-shrink-0">
          <span data-tour="question-counter" className="text-xs font-medium text-gray-400">
            Question {index + 1} of {total}
          </span>
          <button
            data-tour="submit-assessment"
            onClick={onSubmit}
            disabled={submitting}
            className="inline-flex items-center gap-1.5 px-3.5 py-1.5 bg-blue-600 text-white rounded-lg text-xs font-semibold hover:bg-blue-700 disabled:opacity-40 transition-colors"
          >
            {submitting ? <LoadingSpinner size="sm" /> : null}
            {submitting ? 'Submitting…' : 'Submit Assessment'}
          </button>
        </div>
      </div>
      <div className="h-1 bg-gray-100">
        <div
          className="h-full bg-blue-600 transition-all duration-500 ease-out"
          style={{ width: `${progressPct}%` }}
        />
      </div>
    </div>
  )
}

// ============================================================================
// Question card
// ============================================================================

const RESPONSE_MODE_META: Record<string, { icon: React.FC<{ className?: string }>; label: string }> = {
  code_editor: { icon: CodeBracketIcon, label: 'Coding' },
  whiteboard: { icon: PencilSquareIcon, label: 'Design' },
  verbal: { icon: ChatBubbleLeftRightIcon, label: 'Verbal' },
  text: { icon: ChatBubbleLeftRightIcon, label: 'Written' },
}

function QuestionCard({
  token, question, index, total, answerText, onChangeAnswer, markedForReview, onToggleMarkForReview,
  onNext, onTimeUp, onSubmitInterview, isLastQuestion, submitting, whiteboardRef,
  codeLanguage, onCodeLanguageChange, paused, alreadySpentSeconds,
}: {
  token: string
  question: PublicQuestion
  index: number
  total: number
  answerText: string
  onChangeAnswer: (v: string) => void
  markedForReview: boolean
  onToggleMarkForReview: () => void
  onNext: () => void
  onTimeUp: () => void
  onSubmitInterview: () => void
  isLastQuestion: boolean
  submitting: boolean
  whiteboardRef: React.RefObject<WhiteboardCanvasHandle | null>
  codeLanguage: string
  onCodeLanguageChange: (lang: string) => void
  paused?: boolean
  // Real seconds already spent on this exact question in an earlier visit
  // (tracked by the parent's timeSpentRef) — without this, switching away
  // and back reset the countdown to the full duration every time, letting
  // a candidate get unlimited time by bouncing between questions.
  alreadySpentSeconds?: number
}) {
  const [secondsLeft, setSecondsLeft] = useState(
    Math.max(0, question.duration_minutes * 60 - (alreadySpentSeconds || 0))
  )

  useEffect(() => {
    setSecondsLeft(Math.max(0, question.duration_minutes * 60 - (alreadySpentSeconds || 0)))
  }, [question.id, question.duration_minutes, alreadySpentSeconds])

  const dictation = useDictation(answerText, onChangeAnswer)
  useEffect(() => {
    // cancel, not stop: a graceful stop still delivers trailing speech, which
    // would land in the NEW question's answer (e.g. its code editor).
    dictation.cancel()
    // Only reset dictation when the candidate actually moves to a different
    // question — not on every answerText keystroke.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [question.id])

  useEffect(() => {
    // Frozen while the candidate is outside fullscreen — an exit shouldn't
    // silently burn their remaining time on this question.
    if (paused) return
    if (secondsLeft <= 0) {
      onTimeUp()
      return
    }
    const timer = setTimeout(() => setSecondsLeft((s) => s - 1), 1000)
    return () => clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [secondsLeft, paused])

  const minutes = Math.floor(secondsLeft / 60)
  const seconds = secondsLeft % 60
  const totalSeconds = question.duration_minutes * 60
  const urgent = secondsLeft <= 30
  const warn = !urgent && secondsLeft <= totalSeconds * 0.25

  const modeMeta = RESPONSE_MODE_META[question.response_mode] || RESPONSE_MODE_META.text
  const ModeIcon = modeMeta.icon

  const placeholder = useMemo(() => {
    switch (question.response_mode) {
      case 'code_editor': return '// Write your code here…'
      case 'whiteboard': return 'Describe your diagram / design approach step by step…'
      default: return 'Type your answer here…'
    }
  }, [question.response_mode])

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden flex flex-col lg:min-h-[calc(100vh-220px)]">
      <div className="p-6 pb-5">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 bg-blue-50 text-blue-700 rounded-full">
              {question.skill_name}
            </span>
            <span className="inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 bg-gray-100 text-gray-600 rounded-full">
              <ModeIcon className="w-3.5 h-3.5" />
              {modeMeta.label}{question.question_subtype_name ? ` — ${question.question_subtype_name}` : ''}
            </span>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              data-tour="flag-button"
              type="button"
              onClick={onToggleMarkForReview}
              title={markedForReview ? 'Unmark for review' : 'Mark for review'}
              className={`inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1.5 rounded-full border transition-colors ${
                markedForReview ? 'bg-amber-50 border-amber-200 text-amber-700' : 'bg-white border-gray-200 text-gray-400 hover:text-gray-600'
              }`}
            >
              {markedForReview ? <FlagIconSolid className="w-3.5 h-3.5" /> : <FlagIcon className="w-3.5 h-3.5" />}
            </button>
            <span
              data-tour="timer"
              className={`inline-flex items-center gap-1 text-sm font-semibold px-3 py-1.5 rounded-full tabular-nums transition-colors ${
                urgent ? 'bg-red-50 text-red-600' : warn ? 'bg-amber-50 text-amber-600' : 'bg-gray-100 text-gray-600'
              }`}
            >
              <ClockIcon className="w-4 h-4" /> {minutes}:{seconds.toString().padStart(2, '0')}
            </span>
          </div>
        </div>

        <h2 className="text-lg font-medium text-gray-900 leading-relaxed">{question.question_text}</h2>

        {question.test_cases.length > 0 && (
          <div className="mt-4 border border-gray-200 rounded-xl overflow-hidden">
            <div className="px-3.5 py-2 bg-gray-50 border-b border-gray-200">
              <span className="text-xs font-semibold text-gray-600">Example Test Cases</span>
            </div>
            <div className="divide-y divide-gray-100">
              {question.test_cases.map((tc, idx) => (
                <div key={idx} className="px-3.5 py-2.5">
                  <div className="flex flex-wrap gap-x-4 gap-y-1 font-mono text-xs text-gray-700">
                    <span><span className="text-gray-400">Input:</span> {tc.input}</span>
                    <span><span className="text-gray-400">Output:</span> {tc.expected_output}</span>
                  </div>
                  {tc.description && <p className="text-xs text-gray-400 mt-0.5">{tc.description}</p>}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="px-6 pb-6 flex-1 flex flex-col">
        {question.response_mode === 'code_editor' ? (
          <CodeEditor
            token={token}
            questionId={question.id}
            testCases={question.test_cases}
            value={answerText}
            onChange={onChangeAnswer}
            placeholder={placeholder}
            language={codeLanguage}
            onLanguageChange={onCodeLanguageChange}
          />
        ) : question.response_mode === 'whiteboard' ? (
          <div className="space-y-3 flex-1 flex flex-col">
            <WhiteboardCanvas key={question.id} ref={whiteboardRef} />
            <textarea
              value={answerText}
              onChange={(e) => onChangeAnswer(e.target.value)}
              placeholder="Optional: add any notes to go with your sketch…"
              className="w-full flex-1 min-h-[70px] border border-gray-300 rounded-lg px-4 py-2.5 text-sm text-gray-800 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100 resize-y"
            />
          </div>
        ) : (
          <div className="flex-1 flex flex-col">
            <p className="text-xs text-gray-400 mb-1.5">
              {dictation.supported ? 'Type your answer, or use the mic to speak it instead.' : 'Type your answer.'}
            </p>
            <textarea
              value={answerText}
              onChange={(e) => onChangeAnswer(e.target.value)}
              placeholder={placeholder}
              className={`w-full flex-1 min-h-[280px] border rounded-lg px-4 py-3 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-100 resize-y transition-colors ${
                dictation.listening ? 'border-red-300 focus:border-red-400' : 'border-gray-300 focus:border-blue-400'
              }`}
            />
          </div>
        )}
      </div>

      <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex items-center justify-between gap-2">
        {question.response_mode !== 'code_editor' && question.response_mode !== 'whiteboard' && dictation.supported ? (
          <button
            type="button"
            onClick={dictation.listening ? dictation.stop : dictation.start}
            title={dictation.listening ? 'Click to stop' : 'Click to speak your answer'}
            className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition-colors ${
              dictation.listening
                ? 'bg-red-500 text-white shadow-sm shadow-red-200 hover:bg-red-600'
                : 'bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100'
            }`}
          >
            {dictation.listening ? (
              <>
                <span className="relative flex h-2.5 w-2.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75" />
                  <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-white" />
                </span>
                Listening… Click to stop
              </>
            ) : (
              <>
                <MicrophoneIcon className="w-4 h-4" />
                Click to Speak
              </>
            )}
          </button>
        ) : (
          <div />
        )}
        <div className="flex items-center gap-2">
          {!isLastQuestion && (
            <button
              data-tour="next-question"
              onClick={onNext}
              className="inline-flex items-center gap-1.5 px-5 py-2.5 bg-white text-gray-700 border border-gray-300 rounded-xl text-sm font-semibold hover:bg-gray-100 transition-colors"
            >
              Next Question
            </button>
          )}
          {isLastQuestion && (
            <button
              data-tour="next-question"
              onClick={onSubmitInterview}
              disabled={submitting}
              className="inline-flex items-center gap-1.5 px-5 py-2.5 bg-blue-600 text-white rounded-xl text-sm font-semibold hover:bg-blue-700 transition-colors shadow-sm shadow-blue-200 disabled:opacity-60"
            >
              {submitting && <LoadingSpinner size="sm" />}
              {submitting ? 'Submitting…' : 'Submit Interview'}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

// ============================================================================
// Lightweight code editor (dark theme, line numbers, tab-to-indent) — no
// external editor library required, keeps the coding question experience
// professional without pulling in Monaco/CodeMirror for a single textarea.
// ============================================================================

const CODE_LANGUAGES = [
  { id: 'python3', label: 'Python' },
  { id: 'javascript', label: 'JavaScript' },
  { id: 'java', label: 'Java' },
  { id: 'cpp', label: 'C++' },
  { id: 'go', label: 'Go' },
]

export function CodeEditor({
  token, questionId, testCases, value, onChange, placeholder, language, onLanguageChange,
  enableExecution = true, runCodeFn, runTestsFn,
}: {
  token: string
  questionId: string
  testCases: PublicQuestion['test_cases']
  value: string
  onChange: (v: string) => void
  placeholder?: string
  language: string
  onLanguageChange: (lang: string) => void
  // Shows/hides the stdin box, Run buttons and output console. On by default
  // for Proctored Interview; Live Interview's candidate room turns it on too
  // and supplies its own runners (below). Set it false to reuse the editor
  // chrome (line numbers, language picker, dark theme) for pure text capture
  // — nothing crashes without it, since `token`/`questionId` are only used
  // by the default runners.
  enableExecution?: boolean
  // How this editor actually executes code. Both default to Proctored
  // Interview's own token-scoped endpoints, which is what every existing
  // call site wants. Live Interview passes its own pair instead: the two
  // products have separate public routers and separate access tokens, so a
  // Live Interview token posted to /interview/public/... would 404 — but the
  // request/response SHAPES are identical (both backends call the same
  // services/code_execution.py + services/proctored_interview_llm.py), so
  // only the URL needs to vary, not this component's UI.
  runCodeFn?: (code: string, language: string, stdin: string) => Promise<RunCodeResult>
  runTestsFn?: (code: string, language: string) => Promise<RunTestCasesResult>
}) {
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const gutterRef = useRef<HTMLDivElement>(null)
  const lineCount = Math.max(value.split('\n').length, 14)
  const [running, setRunning] = useState(false)
  const [testing, setTesting] = useState(false)
  const [runResult, setRunResult] = useState<RunCodeResult | null>(null)
  const [testResults, setTestResults] = useState<TestCaseRunResult[] | null>(null)
  // Every Run/Run Test Cases click is saved server-side for process-based
  // scoring later — surfaced here so the limit on how many are kept isn't a
  // silent surprise (older attempts drop off past it).
  const [runHistoryCount, setRunHistoryCount] = useState<number | null>(null)
  const [runHistoryLimit, setRunHistoryLimit] = useState<number | null>(null)
  // Custom stdin the candidate can type before hitting Run — independent of
  // a question's own test_cases inputs, fed to the program on this run only.
  const [stdin, setStdin] = useState('')

  // A new question means a new program: clear the console, the test results
  // and the stdin box rather than leaving the previous question's output
  // sitting under the new one. Matters most in Live Interview, where the
  // interviewer reveals one question after another into the SAME mounted
  // editor, so nothing would otherwise reset between them.
  useEffect(() => {
    setRunResult(null)
    setTestResults(null)
    setStdin('')
    setRunHistoryCount(null)
    setRunHistoryLimit(null)
  }, [questionId])

  const syncScroll = () => {
    if (gutterRef.current && textareaRef.current) {
      gutterRef.current.scrollTop = textareaRef.current.scrollTop
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Tab') {
      e.preventDefault()
      const el = e.currentTarget
      const start = el.selectionStart
      const end = el.selectionEnd
      const next = `${value.slice(0, start)}  ${value.slice(end)}`
      onChange(next)
      requestAnimationFrame(() => {
        el.selectionStart = el.selectionEnd = start + 2
      })
    }
  }

  const handleRun = async () => {
    if (!value.trim()) { toast.error('Write some code first.'); return }
    setRunning(true)
    setTestResults(null)
    try {
      const result = runCodeFn
        ? await runCodeFn(value, language, stdin)
        : await runCode(token, questionId, value, language, stdin)
      setRunResult(result)
      if (result.run_history_count != null) setRunHistoryCount(result.run_history_count)
      if (result.run_history_limit != null) setRunHistoryLimit(result.run_history_limit)
    } catch {
      toast.error('Could not run code right now.')
    } finally {
      setRunning(false)
    }
  }

  const handleRunTests = async () => {
    if (!value.trim()) { toast.error('Write some code first.'); return }
    setTesting(true)
    setRunResult(null)
    try {
      const result = runTestsFn
        ? await runTestsFn(value, language)
        : await runTestCases(token, questionId, value, language)
      setTestResults(result.results)
      if (result.run_history_count != null) setRunHistoryCount(result.run_history_count)
      if (result.run_history_limit != null) setRunHistoryLimit(result.run_history_limit)
    } catch {
      toast.error('Could not evaluate test cases right now.')
    } finally {
      setTesting(false)
    }
  }

  return (
    <div className="rounded-lg overflow-hidden border border-gray-800 bg-[#1e1e1e] shadow-inner flex-1 flex flex-col">
      <div className="flex items-center justify-between px-3.5 py-2 bg-[#252526] border-b border-black/40">
        <div className="flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-full bg-[#ff5f56]" />
          <span className="w-2.5 h-2.5 rounded-full bg-[#ffbd2e]" />
          <span className="w-2.5 h-2.5 rounded-full bg-[#27c93f]" />
        </div>
        <select
          value={language}
          onChange={(e) => onLanguageChange(e.target.value)}
          className="bg-transparent text-gray-400 text-xs font-mono border-none outline-none cursor-pointer"
        >
          {CODE_LANGUAGES.map((l) => <option key={l.id} value={l.id} className="bg-[#252526]">{l.label}</option>)}
        </select>
      </div>
      <div className="flex flex-1 min-h-[420px]">
        <div
          ref={gutterRef}
          aria-hidden
          className="select-none text-right pl-4 pr-3 py-3 text-xs text-gray-500 font-mono bg-[#1e1e1e] overflow-hidden"
          style={{ lineHeight: '1.5rem' }}
        >
          {Array.from({ length: lineCount }).map((_, i) => <div key={i}>{i + 1}</div>)}
        </div>
        <textarea
          ref={textareaRef}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onScroll={syncScroll}
          onKeyDown={handleKeyDown}
          spellCheck={false}
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          data-gramm="false"
          data-gramm_editor="false"
          data-enable-grammarly="false"
          placeholder={placeholder}
          className="flex-1 bg-[#1e1e1e] text-gray-100 placeholder-gray-600 font-mono text-sm py-3 pr-4 outline-none resize-none"
          style={{ lineHeight: '1.5rem' }}
        />
      </div>

      {enableExecution && (
      <div className="px-3.5 py-2.5 bg-[#1e1e1e] border-t border-black/40">
        <label className="block text-[10px] font-semibold uppercase tracking-wide text-gray-500 mb-1.5">
          Input (stdin) — optional
        </label>
        <textarea
          value={stdin}
          onChange={(e) => setStdin(e.target.value)}
          spellCheck={false}
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          data-gramm="false"
          data-gramm_editor="false"
          data-enable-grammarly="false"
          placeholder="Type anything your program reads from stdin, one value per line…"
          rows={2}
          className="w-full bg-black/30 text-gray-200 placeholder-gray-600 font-mono text-xs rounded-md px-2.5 py-2 outline-none resize-y border border-gray-800 focus:border-gray-600"
        />
      </div>
      )}

      {enableExecution && (
      <div className="flex items-center gap-2 px-3.5 py-2.5 bg-[#252526] border-t border-black/40">
        <button
          type="button"
          onClick={handleRun}
          disabled={running}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-700 text-gray-100 rounded-md text-xs font-medium hover:bg-gray-600 disabled:opacity-50"
        >
          {running ? <LoadingSpinner size="sm" /> : null} {running ? 'Running…' : '▶ Run'}
        </button>
        {testCases.length > 0 && (
          <button
            type="button"
            onClick={handleRunTests}
            disabled={testing}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-700 text-white rounded-md text-xs font-medium hover:bg-blue-600 disabled:opacity-50"
          >
            {testing ? <LoadingSpinner size="sm" /> : null} {testing ? 'Checking…' : `Run Test Cases (${testCases.length})`}
          </button>
        )}
        <span className="text-[10px] text-gray-500 ml-auto flex items-center gap-2">
          {runHistoryCount != null && runHistoryLimit != null && (
            <span
              className={runHistoryCount >= runHistoryLimit ? 'text-amber-400' : 'text-gray-500'}
              title="Every Run / Run Test Cases attempt is saved and reviewed when your interview is scored — only the most recent attempts are kept."
            >
              {runHistoryCount}/{runHistoryLimit} attempts saved{runHistoryCount >= runHistoryLimit ? ' (oldest will drop)' : ''}
            </span>
          )}
          <span>AI-simulated, not a real compiler/sandbox</span>
        </span>
      </div>
      )}

      {enableExecution && runResult && (
        <div className="px-3.5 py-3 bg-black/30 border-t border-black/40 font-mono text-xs space-y-1">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-500 mb-1">Output</p>
          {runResult.error ? (
            <p className="text-red-400">{runResult.error}</p>
          ) : (
            <>
              {runResult.stdout && <pre className="text-gray-200 whitespace-pre-wrap">{runResult.stdout}</pre>}
              {runResult.stderr && <pre className="text-red-400 whitespace-pre-wrap">{runResult.stderr}</pre>}
              {!runResult.stdout && !runResult.stderr && <p className="text-gray-500">(no output)</p>}
            </>
          )}
        </div>
      )}

      {enableExecution && testResults && (
        <div className="px-3.5 py-3 bg-black/30 border-t border-black/40 space-y-1.5">
          {testResults.map((r, idx) => (
            <div key={idx} className="flex items-start gap-2 text-xs">
              <span className={`px-1.5 py-0.5 rounded font-mono font-medium flex-shrink-0 ${r.passed ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
                {r.passed ? 'PASS' : 'FAIL'}
              </span>
              <span className="text-gray-300">{r.description}{r.explanation ? ` — ${r.explanation}` : ''}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
